import type { en } from './en';

/**
 * Russian strings. Typed as `Record<keyof typeof en, string>` - every key in
 * `en.ts` MUST have an entry here, and vice versa; a mismatch fails
 * `tsc`, not at runtime. Product wording, not a literal/machine
 * translation - uses the terms a Russian-speaking warehouse/fulfilment
 * operator would actually expect (приёмка/отгрузка/остатки/резерв etc.),
 * matching the vocabulary already established in this repo's own Russian
 * DomainError catalog (packages/foundation) where the two overlap.
 */
export const ru: Record<keyof typeof en, string> = {
  // Nav / shell
  'nav.brand': 'Baltic Fulfilment · Опер.',
  'nav.skipToContent': 'Перейти к содержимому',
  'nav.openMenu': 'Открыть меню',
  'nav.closeMenu': 'Закрыть меню',
  'nav.overview': 'Обзор',
  'nav.onboarding': 'Настройка',
  'nav.queue': 'Очередь',
  'nav.sellers': 'Продавцы',
  'nav.inventory': 'Остатки',
  'nav.inbounds': 'Приёмки',
  'nav.orders': 'Заказы',
  'nav.shipments': 'Отгрузки',
  'nav.returns': 'Возвраты',
  'nav.billing': 'Биллинг',
  'nav.imports': 'Импорт',
  'nav.users': 'Сотрудники и роли',
  'nav.integrations': 'Интеграции',
  'nav.subscription': 'Подписка',
  'nav.problems': 'Проблемы',
  'nav.settings': 'Настройки',
  'nav.logOut': 'Выйти',
  'nav.checkingSession': 'Проверяем сессию…',

  // Common
  'common.loading': 'Загрузка…',
  'common.retry': 'Повторить',
  'common.save': 'Сохранить',
  'common.saving': 'Сохранение…',
  'common.cancel': 'Отмена',
  'common.close': 'Закрыть',
  'common.create': 'Создать',
  'common.edit': 'Изменить',
  'common.delete': 'Удалить',
  'common.confirm': 'Подтвердить',
  'common.back': 'Назад',
  'common.next': 'Далее',
  'common.previous': 'Назад',
  'common.of': 'из',
  'common.search': 'Поиск',
  'common.filter': 'Фильтр',
  'filters.aria': 'Фильтры списка',
  'filters.all': 'Все',
  'filters.allSellers': 'Все продавцы',
  'filters.allWarehouses': 'Все склады',
  'filters.from': 'С',
  'filters.to': 'По',
  'filters.sort': 'Сортировка по дате',
  'filters.newest': 'Сначала новые',
  'filters.oldest': 'Сначала старые',
  'filters.reset': 'Сбросить фильтры',
  'filters.rangeInvalid': 'Дата начала позже даты окончания: применяется только дата начала.',
  'filters.created': 'Создано',
  'filters.noMatch': 'По этим фильтрам ничего не найдено.',
  'common.status': 'Статус',
  'common.actions': 'Действия',
  'common.reason': 'Причина',
  'common.note': 'Примечание',
  'common.required': 'Обязательно',
  'common.optional': 'Необязательно',
  'common.yes': 'Да',
  'common.no': 'Нет',
  'common.none': 'Нет',
  'common.unknown': 'Неизвестно',
  'common.total': 'Итого',
  'common.recorded': 'Записано',
  'common.nothingHereYet': 'Здесь пока пусто',
  'common.hide': 'Скрыть',
  'common.noAccess': 'У вас нет доступа к этому разделу',

  // Generic errors
  'error.unexpected': 'Произошла непредвиденная ошибка. Попробуйте ещё раз или вернитесь позже.',
  'error.roleLastAdmin': 'В организации не останется ни одного администратора. Сначала назначьте другого ADMIN, затем измените этого.',
  'error.roleUnknown': 'Такой роли не существует.',
  'error.forbidden': 'У вас нет прав для этого действия.',
  'error.notFound': 'Запись не найдена — возможно, она была удалена.',
  'error.sessionExpired': 'Сессия больше не действительна. Войдите заново.',
  'error.tooManyRequests': 'Слишком много запросов — подождите немного и попробуйте снова.',
  'error.serverUnexpected': 'На сервере произошла непредвиденная ошибка.',
  'error.requestFailed': 'Не удалось выполнить запрос.',

  // Status / domain-state labels (StatusBadge)
  'status.DRAFT': 'Черновик',
  'status.BUILDING': 'Формируется',
  'status.PICKING': 'Сборка',
  'status.PICKED': 'Собран',
  'status.PACKING': 'Упаковка',
  'status.PACKED': 'Упакован',
  'status.READY': 'Готов к отгрузке',
  'status.SHIPPED': 'Отгружен',
  'status.CANCELLED': 'Отменён',
  'status.CANCELLED_AFTER_PICK': 'Отменён (после сборки)',
  'status.NEW': 'Новый',
  'status.RESERVED': 'Зарезервирован',
  'status.ARRIVED': 'Прибыл',
  'status.RECEIVING': 'Приёмка',
  'status.RECEIVED': 'Принят',
  'status.CLOSED': 'Закрыт',
  'status.ACTIVE': 'Активен',
  'status.SUSPENDED': 'Приостановлен',
  'status.REVOKED': 'Отозван',
  'status.DISABLED': 'Отключён',
  'status.UNDECIDED': 'Не определено',
  'status.NONE': 'Нет',
  'status.SHORT': 'Недостача',
  'status.OVER': 'Излишек',
  'status.DAMAGED': 'Повреждён',
  'status.MISMATCH': 'Несоответствие',
  'status.RESTOCK': 'Возвращён на склад',
  'status.QUARANTINE': 'На карантине',
  'status.WRITE_OFF': 'Списан',
  'status.AWAITING_DISPOSITION': 'Ожидает решения',
  'status.ON_HOLD': 'Приостановлено',
  'status.TERMINATED': 'Прекращён',
  'status.RECEIVING_DISCREPANCY': 'Расхождение при приёмке',
  'status.RETURN_AWAITING_DISPOSITION': 'Возврат ожидает решения',
  'status.IMPORT_CONFLICT': 'Конфликт импорта',
  'status.BILLING_UNPRICED': 'Услуга без тарифа',
  'status.SHIPMENT_INCOMPLETE': 'Отгрузка не завершена',
  'status.NO_TARIFF_RULE': 'Нет тарифного правила',
  'status.normal': 'Срок: в норме',
  'status.approaching_cutoff': 'Срок: подходит',
  'status.urgent': 'Срок: срочно',
  'status.blocked': 'Заблокировано',
  'status.blockedProblem': 'Есть проблема',
  'status.overdue': 'Срок: просрочено',
  'status.SHORT_PICK': 'Недобор при сборке',
  'status.MISSING_MARKING': 'Нет маркировки',
  'status.MISSING_LABEL': 'Нет этикетки',
  'status.INCOMPLETE_PACKAGE': 'Упаковка не завершена',
  'status.CANCELLATION': 'Отменён после сборки',
  'status.DEADLINE_CHANGED': 'Срок изменён',
  'status.NOT_STARTED': 'Не начато',
  'status.IN_PROGRESS': 'В процессе',
  'status.BLOCKED': 'Заблокировано',
  'status.READY_FOR_REVIEW': 'Готово к проверке',
  'status.RECEIPT': 'Поступление',
  'status.PICK': 'Сборка',
  'status.PACK': 'Упаковка',
  'status.PUTAWAY': 'Размещение',
  'status.SHIP': 'Отгрузка',
  'status.MOVE': 'Перемещение',
  'status.RETURN_RECEIPT': 'Приёмка возврата',
  'status.CORRECTION': 'Корректировка',
  'status.SUPPLY': 'Поставка',
  'status.RETURN': 'Возврат',
  'status.TRANSFER_IN': 'Внутреннее перемещение',
  'status.FOUND': 'Найдено на складе',
  'status.WAREHOUSE': 'Склад',
  'status.OWNER': 'Продавец',
  'status.CARRIER': 'Перевозчик',
  'status.CHANNEL': 'Канал',
  'status.DAMAGE': 'Повреждение',
  'status.UNKNOWN_ITEM': 'Неопознанный товар',
  'status.RETURN_INSPECTION': 'Осмотр возврата',
  'status.RECEIVING_UNIT': 'Приёмка',
  'status.RECEIVING_DOC': 'Документ приёмки',
  'status.PICKING_UNIT': 'Сборка',
  'status.PACKING_UNIT': 'Упаковка',
  'status.PACKAGE_MATERIAL': 'Упаковочный материал',
  'status.PALLET_HANDLING': 'Работа с паллетой',
  'status.STORAGE_LOCATION': 'Хранение',
  'status.RETURN_UNIT': 'Возврат',
  'status.MARKING_SCAN': 'Сканирование маркировки',
  'status.RELABEL': 'Переэтикетирование',
  'status.DISPOSAL': 'Утилизация',
  'status.MANUAL_HANDLING': 'Ручная обработка',
  'status.PENDING': 'Ожидает',
  'status.REQUESTED': 'Запрошено',
  'status.PRINTING': 'Печатается',
  'status.PRINTED': 'Напечатано',
  'status.FAILED': 'Ошибка',
  'status.EXPECTED': 'Ожидается',
  'status.ASSIGNED': 'Назначено',
  'status.DONE': 'Готово',
  'status.DEPARTED': 'Отправлено',
  'status.ACCEPTED': 'Принято',
  'status.REJECTED': 'Отклонено',
  'status.OPEN': 'Открыт',
  'status.ISSUED': 'Выставлен',
  'status.PAID': 'Оплачен',
  'status.PARTIALLY_PAID': 'Частично оплачен',
  'status.OVERDUE': 'Просрочен',
  'status.SENT': 'Отправлен',
  'status.DISPUTED': 'Оспорен',
  'status.VOID': 'Аннулирован',

  // Overview
  'overview.title': 'Обзор',
  'overview.subtitle': 'Что требует внимания прямо сейчас',
  'overview.openInbounds': 'Открытые приёмки',
  'overview.ordersToPick': 'Заказы на сборку',
  'overview.ordersToPack': 'Заказы на упаковку',
  'overview.readyToShip': 'Готовы к отгрузке',
  'overview.returnsAwaitingDisposition': 'Возвраты, ожидающие решения',
  'overview.importConflicts': 'Конфликты импорта',
  'overview.billingExceptions': 'Проблемы биллинга',
  'overview.problemOperations': 'Проблемные операции',
  'overview.needsAttention': 'Требует внимания',
  'overview.nothingNeedsAttention': 'Сейчас ничего не требует внимания',
  'overview.colType': 'Тип',
  'overview.colEntity': 'Объект',
  'overview.colSeller': 'Продавец',
  'overview.colSeverity': 'Важность',
  'overview.colAge': 'Возраст',
  'overview.ageDays': '{n} дн.',
  'overview.ageHours': '{n} ч.',
  'overview.ageMinutes': '{n} мин.',

  // Queue
  'queue.title': 'Очередь',
  'queue.sectionsAriaLabel': 'Разделы очереди',
  'queue.toPick': 'На сборку',
  'queue.toPack': 'На упаковку',
  'queue.urgent': 'Срочные',
  'queue.problems': 'Проблемы',
  'queue.emptyTitle': 'Здесь пусто',
  'queue.emptyHint': 'В этом разделе сейчас нет заказов.',
  'queue.colOrder': 'Заказ',
  'queue.colState': 'Статус',
  'queue.colChannel': 'Канал',
  'queue.colPriority': 'Статус срока',
  'queue.colDeadline': 'Срок',
  'queue.colBlockers': 'Блокеры',
  'queue.channelManual': 'Вручную',
  'queue.deadlineEstimated': '{deadline} (оценочно)',

  // Inventory
  'inventory.title': 'Остатки',
  'inventory.searchPlaceholder': 'Поиск по коду или названию SKU…',
  'inventory.searchAriaLabel': 'Поиск по коду или названию SKU',
  'inventory.emptyTitle': 'Нет остатков по заданным фильтрам',
  'inventory.colSku': 'SKU',
  'inventory.colSeller': 'Продавец',
  'inventory.colLocation': 'Ячейка',
  'inventory.colOnHand': 'На складе',
  'inventory.colReserved': 'В резерве',
  'inventory.colAvailable': 'Доступно',
  'inventory.colLastMovement': 'Последнее движение',
  'inventory.colBucket': 'Статус запаса',
  'inventory.colPhysical': 'Физически на складе',
  'inventory.bucket.BIN': 'В ячейке',
  'inventory.bucket.QUARANTINE': 'Карантин',
  'inventory.bucket.IN_PROCESS': 'В обработке (приёмка/сборка/упаковка)',
  'inventory.bucket.SHIPPED': 'Отгружено (вне склада)',
  'inventory.summary.title': 'Сводка по запасам',
  'inventory.summary.inBins': 'Физически в ячейках',
  'inventory.summary.available': 'Доступно (к отбору за вычетом резерва)',
  'inventory.summary.quarantine': 'Карантин',
  'inventory.summary.inProcess': 'В обработке (приёмка/сборка/упаковка)',
  'inventory.summary.shipped': 'Отгружено (не в наличии)',

  // Inventory detail
  'inventoryDetail.serialTracked': 'Серийный учёт / КИЗ',
  'inventoryDetail.lotTracked': 'Партионный учёт',
  'inventoryDetail.balancesByLocation': 'Остатки по ячейкам',
  'inventoryDetail.noBalances': 'Остатков нет.',
  'inventoryDetail.colWarehouse': 'Склад',
  'inventoryDetail.colLocation': 'Ячейка',
  'inventoryDetail.openReservations': 'Открытые резервы',
  'inventoryDetail.noReservations': 'Открытых резервов нет.',
  'inventoryDetail.colQty': 'Кол-во',
  'inventoryDetail.colCreated': 'Создан',
  'inventoryDetail.colExpires': 'Истекает',
  'inventoryDetail.movementHistory': 'История движений (только чтение)',
  'inventoryDetail.noMovements': 'Движений не зафиксировано.',
  'inventoryDetail.colReason': 'Причина',
  'inventoryDetail.colDocument': 'Документ',
  'inventoryDetail.colOccurred': 'Дата',

  // Settings
  'settings.title': 'Настройки',
  'settings.yourSession': 'Ваша сессия',
  'settings.role': 'Роль',
  'settings.warehouse': 'Склад',
  'settings.orgWide': 'По всей организации',
  'settings.notSignedIn': 'Вы не авторизованы.',

  // Office roles
  'role.ADMIN': 'Администратор',
  'role.WAREHOUSE': 'Кладовщик',
  'role.PICKER': 'Сборщик',
  'role.PACKER': 'Упаковщик',
  'role.BILLING': 'Биллинг',
  'role.SUPPORT': 'Поддержка',

  // Inbounds list
  'inbounds.colNumber': '№ приёмки',
  'inbounds.colSeller': 'Продавец',
  'inbounds.colWarehouse': 'Склад',
  'inbounds.colExpected': 'Ожидается',
  'inbounds.colReceived': 'Принято',
  'inbounds.colDiscrepancies': 'Расхождения',
  'inbounds.filterByStatus': 'Фильтр по статусу',
  'inbounds.allStatuses': 'Все статусы',
  'inbounds.discrepanciesOnly': 'Только с расхождениями',
  'inbounds.emptyTitle': 'Нет приёмок по заданным фильтрам',

  // Inbound detail
  'inboundDetail.title': 'Приёмка {number}',
  'inboundDetail.kind': 'Тип',
  'inboundDetail.source': 'Кто создал',
  'inboundDetail.sourceSellerPortal': 'Заявка продавца (кабинет продавца)',
  'inboundDetail.arrived': 'Прибыла',
  'inboundDetail.lines': 'Строки',
  'inboundDetail.noLines': 'Строк не зафиксировано.',
  'inboundDetail.discrepancy': 'Расхождение',
  'inboundDetail.responsibility': 'Ответственность',
  'inboundDetail.evidence': 'Фото',
  'inboundDetail.photos': 'Фото',
  'inboundDetail.dispositionHistory': 'История решений / фото',
  'inboundDetail.noDispositions': 'По этой приёмке решений не зафиксировано.',
  'inboundDetail.colQty': 'Кол-во',
  'inboundDetail.colDisposition': 'Решение',
  'inboundDetail.colToLocation': 'В ячейку',
  'inboundDetail.colRecorded': 'Зафиксировано',
  'inboundDetail.colReversed': 'Отменено',

  // Orders list
  'orders.colNumber': '№ заказа',
  'orders.colReserved': 'Резерв',
  'orders.colPicked': 'Собрано',
  'orders.colPacked': 'Упаковано',
  'orders.colDue': 'Срок',
  'orders.emptyTitle': 'Нет заказов по заданным фильтрам',

  // Order detail
  'orderDetail.title': 'Заказ {number}',
  'orderDetail.noLines': 'Строк нет.',
  'orderDetail.colOrdered': 'Заказано',
  'orderDetail.colShipped': 'Отгружено',
  'orderDetail.activityTimeline': 'Хронология действий',
  'orderDetail.noActivity': 'Действий не зафиксировано.',
  'orderDetail.byActor': 'от {actor}',

  // Shipments list
  'shipments.colNumber': '№ отгрузки',
  'shipments.colPackages': 'Коробки',
  'shipments.colCarrier': 'Перевозчик',
  'shipments.colShippedAt': 'Дата отгрузки',
  'shipments.notReadyOnly': 'Только не готовые / незавершённые',
  'shipments.emptyTitle': 'Нет отгрузок по заданным фильтрам',

  // Shipment detail
  'shipmentDetail.title': 'Отгрузка {number}',
  'shipmentDetail.carrierRef': 'Номер у перевозчика',
  'shipmentDetail.departed': 'Отправлена',
  'shipmentDetail.notYetDeparted': 'Ещё не отправлена',
  'shipmentDetail.packagesCount': 'Коробки ({n})',
  'shipmentDetail.noPackages': 'Коробки ещё не сформированы.',
  'shipmentDetail.colCode': 'Код',
  'shipmentDetail.colWeight': 'Вес (г)',
  'shipmentDetail.colContents': 'Содержимое',
  'shipmentDetail.colLabel': 'Этикетка',
  'shipmentDetail.custodyExit': 'Выбытие со склада (движение SHIP)',
  'shipmentDetail.custodyNotExited': 'Товар ещё не выбыл со склада — отгрузка не состоялась.',
  'shipmentDetail.requestLabel': 'Запросить транспортную этикетку',
  'shipmentDetail.printPackageLabel': 'Печать этикетки места',
  'shipmentDetail.printPackageLabelA4': 'PDF A4 (обычный принтер)',
  'shipmentDetail.noTransportLabel': 'Транспортная этикетка маркетплейса не нужна: заказ создан не через подключение маркетплейса. Печатайте этикетку места.',
  'shipmentDetail.print': 'Печать',
  'shipmentDetail.retryPrint': 'Повторить печать',
  'shipmentDetail.lastError': 'Последняя ошибка: {code}',
  'shipmentDetail.requestFailed': 'Не удалось выполнить запрос',
  'shipmentDetail.printFailed': 'Не удалось напечатать',

  // Returns list
  'returns.colNumber': '№ возврата',
  'returns.colDispositioned': 'Решение принято',
  'returns.colUnresolved': 'Без решения',
  'returns.colReceivedAt': 'Дата приёмки',
  'returns.awaitingDisposition': 'Ожидает решения',
  'returns.filterByDisposition': 'Фильтр по решению',
  'returns.allDispositions': 'Все решения',
  'returns.emptyTitle': 'Нет возвратов по заданным фильтрам',

  // Return detail
  'returnDetail.title': 'Возврат {number}',
  'returnDetail.whatCameBack': 'Что вернулось',
  'returnDetail.colQtyReceived': 'Принято, кол-во',
  'returnDetail.colCondition': 'Состояние',
  'returnDetail.dispositionDecisions': 'Решения по возврату',
  'returnDetail.awaitingDisposition': 'Ожидает решения — решение ещё не принято.',
  'returnDetail.colDecidedBy': 'Кем принято',
  'returnDetail.colTimestamp': 'Время',
  'returnDetail.colPostLocation': 'Ячейка после решения',

  // Login
  'login.title': 'Вход в WMS Office',
  'login.subtitle':
    'Вставьте bearer-токен, выданный для вашей учётной записи. Администратор выдаёт его в разделе «Пользователи и роли», либо оператор выпускает первый токен через office bootstrap CLI.',
  'login.bearerToken': 'Bearer-токен',
  'login.signIn': 'Войти',
  'login.signingIn': 'Выполняется вход…',
  'login.tokenInvalid': 'Токен недействителен, просрочен или отозван.',
  'login.couldNotSignIn': 'Не удалось войти. Попробуйте снова.',

  // Billing
  'billing.tabOverview': 'Обзор',
  'billing.tabContracts': 'Договоры',
  'billing.tabTariffs': 'Тарифы',
  'billing.tabCharges': 'Начисления',
  'billing.tabInvoices': 'Счета',
  'billing.tabManualService': 'Новая ручная услуга',
  'billing.tabLegalEntitySettings': 'Настройки юрлица',
  'billing.tabDataExport': 'Экспорт данных',
  'billing.permissionDenied': 'Биллинг доступен ролям ADMIN, BILLING и SUPPORT (у SUPPORT — только чтение).',
  'billing.manualServicePermissionDenied': 'Записывать ручную услугу могут только ADMIN/BILLING.',
  'billing.dataExportPermissionDenied': 'Экспорт данных организации доступен только ADMIN.',
  'billing.sellerServiceBreakdowns': 'Разбивка по продавцам/услугам за период',
  'billing.unpricedBillableEvents': 'Услуги без тарифа',
  'billing.operationsWithNoCharge': 'Операции без начисления',
  'billing.everyOperationCharged': 'По каждой физической операции есть начисление',
  'billing.noContractsYet': 'Договоров пока нет',
  'billing.tariff': 'Тариф',
  'billing.currency': 'Валюта',
  'billing.effectiveFrom': 'Действует с',
  'billing.colCode': 'Код',
  'billing.colKind': 'Тип',
  'billing.colRate': 'Ставка',
  'billing.colUnit': 'Единица',
  'billing.priceAService': 'Установить цену услуги',
  'billing.priceAServiceHint': 'Никогда не изменяется на месте — текущее правило закрывается, открывается новое, действующее с этого момента.',
  'billing.serviceCode': 'Код услуги',
  'billing.saveNewRate': 'Сохранить новую ставку',
  'billing.noChargesYet': 'Начислений пока нет',
  'billing.colDate': 'Дата',
  'billing.colAmount': 'Сумма',
  'billing.colSource': 'Источник',
  'billing.manual': 'Вручную',
  'billing.hideEvidence': 'Скрыть детали',
  'billing.chargeEvidence': 'Детали начисления',
  'billing.physicalOperation': 'Физическая операция',
  'billing.noneManualEntry': 'Нет (ручная запись)',
  'billing.businessDocument': 'Документ',
  'billing.actorDevice': 'Исполнитель / устройство',
  'billing.tariffRuleSnapshot': 'Применённое тарифное правило',
  'billing.noTariffRuleApplied': 'Тарифное правило не применялось',
  'billing.calculation': 'Расчёт',
  'billing.reasonNote': 'Причина / примечание',
  'billing.newManualService': 'Новая ручная услуга',
  'billing.newManualServiceHint': 'Причина и подтверждение обязательны — это реальное, проверяемое начисление, а не просто поле с суммой.',
  'billing.sellerOwnerId': 'Продавец',
  'billing.selectSeller': 'Выберите продавца',
  'billing.quantity': 'Количество',
  'billing.rateInfoOnly': 'Ставка (справочно — тарифные правила всё равно применяются)',
  'billing.reasonRequired': 'Причина (обязательно)',
  'billing.referenceDocKind': 'Тип документа-основания (необязательно)',
  'billing.recording': 'Записываем…',
  'billing.recordCharge': 'Записать начисление',
  'billing.allSellers': 'Все продавцы',
  'billing.statusIssued': 'Выставлен',
  'billing.statusPartiallyPaid': 'Частично оплачен',
  'billing.statusPaid': 'Оплачен',
  'billing.statusVoid': 'Аннулирован',
  'billing.noInvoicesMatch': 'Нет счетов по заданным фильтрам',
  'billing.colNumber': 'Номер',
  'billing.colPeriod': 'Период',
  'billing.generateDraftInvoice': 'Сформировать черновик счёта',
  'billing.generateDraftInvoiceHint':
    'Забирает все ещё не выставленные начисления по этому договору за указанный период. Повторный вызов на том же открытом черновике безопасен — он только добавляет новые начисления, никогда не дублирует и не переписывает существующие строки.',
  'billing.contract': 'Договор',
  'billing.selectContract': 'Выберите договор…',
  'billing.periodStart': 'Начало периода',
  'billing.periodEnd': 'Конец периода',
  'billing.generating': 'Формируем…',
  'billing.generateRegenerateDraft': 'Сформировать / обновить черновик',
  'billing.subtotal': 'Сумма без налога',
  'billing.tax': 'Налог',
  'billing.paid': 'Оплачено',
  'billing.outstandingBalance': 'Остаток к оплате',
  'billing.linesTraceable': 'Строки — каждая привязана к своему начислению',
  'billing.description': 'Описание',
  'billing.sourceCharge': 'Начисление-источник',
  'billing.downloadPdf': 'Скачать PDF',
  'billing.issue': 'Выставить',
  'billing.voidReason': 'Причина аннулирования',
  'billing.void': 'Аннулировать',
  'billing.recordPaymentLabel': 'Записать оплату (ручная сверка — платёжного шлюза нет)',
  'billing.amountInMinorUnits': 'Сумма в минимальных единицах валюты',
  'billing.recordPayment': 'Записать оплату',
  'billing.adjustmentLabel': 'Корректировка (кредит-нота/дебет-нота — не переписывает уже выставленную строку)',
  'billing.creditNote': 'Кредит-нота',
  'billing.debitNote': 'Дебет-нота',
  'billing.reasonRequiredPlaceholder': 'Причина (обязательно)',
  'billing.apply': 'Применить',
  'billing.legalEntity': 'Юрлицо',
  'billing.selectLegalEntity': 'Выберите юрлицо…',
  'billing.noLegalEntitiesYet': 'Юрлиц пока нет — сначала создайте его в разделе «Настройка».',
  'billing.taxVatConfig': 'Настройка налога / НДС',
  'billing.taxVatConfigHint':
    'Ничего не додумывается: юрлицо без настройки здесь не начисляет налог на выставленных счетах — это отличается от явной записи «освобождён», обе ситуации видны ниже и никогда не путаются.',
  'billing.currentlyActive': 'Сейчас действует',
  'billing.exempt': 'Освобождён',
  'billing.noTaxConfigOnFile': 'Настройки налога нет — счета этого юрлица не содержат налог, пока она не добавлена.',
  'billing.jurisdiction': 'Юрисдикция',
  'billing.active': 'Активна',
  'billing.addNewVersion': 'Добавить новую версию',
  'billing.jurisdictionIso': 'Юрисдикция (код страны ISO)',
  'billing.exemptNoVat': 'Освобождён (без НДС)',
  'billing.rateFraction': 'Ставка (доля, например 0.19 для 19%)',
  'billing.taxVatRegNumber': 'Номер налоговой/НДС регистрации',
  'billing.label': 'Название',
  'billing.saveNewTaxConfig': 'Сохранить новую настройку налога',
  'billing.invoiceLegalTemplate': 'Юридический шаблон счёта',
  'billing.invoiceLegalTemplateHint': 'Фиксируется в каждом счёте в момент выставления — изменение здесь не меняет уже выставленные счета.',
  'billing.sellerLegalName': 'Юридическое название продавца',
  'billing.registrationNumber': 'Регистрационный номер',
  'billing.taxVatId': 'ИНН / номер НДС',
  'billing.noInvoiceTemplateOnFile': 'Шаблона счёта нет — выставленные счета используют общий резервный вариант оформления.',
  'billing.stillMissing': 'Для завершения шаблона не хватает: {fields}.',
  'billing.saveNewVersion': 'Сохранить новую версию',
  'billing.footerText': 'Текст в футере',
  'billing.paymentTerms': 'Условия оплаты',
  'billing.pdfLanguage': 'Язык PDF-счёта',
  'billing.pdfLanguageEn': 'English (по умолчанию)',
  'billing.pdfLanguageRu': 'Русский — «Счёт на оплату»',
  'billing.legalForm': 'Форма',
  'billing.legalFormIp': 'ИП',
  'billing.legalFormOoo': 'ООО / организация',
  'billing.kpp': 'КПП (только для организации)',
  'billing.ruAddress': 'Юридический адрес',
  'billing.bankName': 'Банк получателя',
  'billing.bik': 'БИК',
  'billing.settlementAccount': 'Расчётный счёт',
  'billing.correspondentAccount': 'Корреспондентский счёт',
  'billing.signerPosition': 'Должность подписанта',
  'billing.signerName': 'Подписант (Ф. И. О.)',
  'billing.paymentTermsPlaceholder': 'например, 14 дней',
  'billing.saveNewTemplateVersion': 'Сохранить новую версию шаблона',
  'billing.fullOrgExport': 'Полный экспорт организации',
  'billing.fullOrgExportHint':
    'Полный JSON-экспорт данных организации в её собственном контуре — юрлица, склады, каталог, остатки, заказы/отгрузки/возвраты, биллинг, метаданные пользователей (без учётных данных), журнал аудита и манифест доказательств (ключи хранилища, но не сами фото).',
  'billing.generateNewExport': 'Сформировать новый экспорт',
  'billing.previousExports': 'Предыдущие экспорты',
  'billing.noExportGenerated': 'Экспорт ещё не формировался',
  'billing.colGenerated': 'Сформирован',
  'billing.colSize': 'Размер',
  'billing.sizeKb': '{kb} КБ',
  'billing.download': 'Скачать',

  // Onboarding
  'onboarding.stepOrgProfileTitle': 'Профиль организации',
  'onboarding.stepOrgProfileDesc': 'Создаётся автоматически при регистрации организации.',
  'onboarding.stepLegalEntityTitle': 'Юрлицо',
  'onboarding.stepLegalEntityDesc': 'Юрлицо, от имени которого выставляются счета.',
  'onboarding.stepWarehouseTitle': 'Склад',
  'onboarding.stepWarehouseDesc': 'Хотя бы один физический склад.',
  'onboarding.stepWarehouseTimezoneTitle': 'Часовой пояс склада',
  'onboarding.stepWarehouseTimezoneDesc': 'Каждому складу нужен часовой пояс IANA для дедлайнов и смен.',
  'onboarding.stepSystemLocationsTitle': 'Системные ячейки',
  'onboarding.stepSystemLocationsDesc': 'Ячейки RECEIVING, PACKING и QUARANTINE для каждого склада.',
  'onboarding.stepFirstSellerTitle': 'Первый продавец',
  'onboarding.stepFirstSellerDesc': 'Хотя бы один продавец в системе.',
  'onboarding.stepSellerContractTitle': 'Договор с продавцом',
  'onboarding.stepSellerContractDesc': 'Договор, связывающий продавца с юрлицом.',
  'onboarding.stepTariffAssignmentTitle': 'Назначение тарифа',
  'onboarding.stepTariffAssignmentDesc': 'Требуется только если для организации включён расширенный биллинг.',
  'onboarding.stepInternalUsersTitle': 'Внутренние пользователи',
  'onboarding.stepInternalUsersDesc': 'Хотя бы одна учётная запись с ролью ADMIN.',
  'onboarding.stepSellerPortalUsersTitle': 'Доступ в портал продавца',
  'onboarding.stepSellerPortalUsersDesc': 'Проверяется только для продавцов, у которых уже настроен вход в портал.',
  'onboarding.stepInitialImportTitle': 'Первоначальный импорт данных',
  'onboarding.stepInitialImportDesc': 'Необязательно — массовый импорт продавцов/SKU/ячеек/остатков через CSV.',
  'onboarding.stepReconciliationTitle': 'Сверка начальных остатков',
  'onboarding.stepReconciliationDesc': 'Завершённая партия импорта STOCK_BALANCE, либо явный, зафиксированный пропуск.',
  'onboarding.notRequired': 'Не требуется',
  'onboarding.goFixThis': 'Перейти и исправить',
  'onboarding.setupOrgStructure': 'Настройка структуры организации',
  'onboarding.step1LegalEntity': '1. Юрлицо',
  'onboarding.name': 'Название',
  'onboarding.countryIso2': 'Страна (ISO-2)',
  'onboarding.currencyIso3': 'Валюта (ISO-3)',
  'onboarding.createLegalEntity': 'Создать юрлицо',
  'onboarding.step2Warehouse': '2. Склад',
  'onboarding.timezoneIana': 'Часовой пояс (IANA)',
  'onboarding.createWarehouse': 'Создать склад',
  'onboarding.step3SystemLocations': '3. Системные ячейки (приёмка / упаковка / карантин)',
  'onboarding.selectWarehouse': 'Выберите склад…',
  'onboarding.createSystemLocations': 'Создать недостающие системные ячейки',
  'onboarding.step4FirstSeller': '4. Первый продавец',
  'onboarding.createSeller': 'Создать продавца',
  'onboarding.otherStepsHint':
    'Договоры с продавцами и назначение тарифов — на странице «Биллинг»; внутренние пользователи — в разделе «Пользователи и роли»; вход в портал продавца — на странице самого продавца. Выполнение шага в любом месте продукта обновляет чек-лист выше.',
  'onboarding.permissionDenied': 'Настройка и готовность организации видны только ADMIN.',
  'onboarding.stepsComplete': 'Выполнено {done} из {total} обязательных шагов.',
  'onboarding.colStep': 'Шаг',
  'onboarding.skipReconciliation': 'Пропустить сверку начальных остатков',
  'onboarding.skipReconciliationHint':
    'Делайте это только если начальные остатки были внесены другим способом (или их правда ещё нет). Действие фиксируется в журнале и требует указания причины.',
  'onboarding.skipWithReason': 'Пропустить с указанием причины',
  'onboarding.reconciliationSkippedNote': 'Сверка была явно пропущена {at}: «{reason}».',
  'onboarding.activation': 'Активация',
  'onboarding.activatedOn': 'Организация активирована {at}.',
  'onboarding.everyCheckPasses': 'Все обязательные проверки пройдены. Активация — необратимое, фиксируемое в журнале действие.',
  'onboarding.blockersRemain': 'Осталось блокеров: {n} — активация недоступна, пока все не будут устранены.',
  'onboarding.activateOrganisation': 'Активировать организацию',

  // Users & Roles
  'users.rolesNote': 'BILLING не может выполнять складские операции, SUPPORT не может менять тарифы, а последнего ADMIN организации нельзя понизить в роли или приостановить.',
  'users.noMembersYet': 'Пользователей пока нет',
  'users.email': 'Email',
  'users.roleForAriaLabel': 'Роль для {name}',
  'users.cannotChangeOwnRole': 'Вы не можете изменить собственную роль (запрет самоповышения).',
  'users.suspend': 'Приостановить',
  'users.reactivate': 'Восстановить',

  // Channels / Integrations
  'channels.credentialsHint': 'Учётные данные шифруются при хранении и никогда не показываются полностью — только подсказка для распознавания (например, «****1234»).',
  'channels.emptyTitle': 'Каналы маркетплейсов ещё не подключены',
  'channels.colMarketplace': 'Маркетплейс',
  'channels.colConnection': 'Подключение',
  'channels.colCredential': 'Учётные данные',
  'channels.colSyncStatus': 'Статус синхронизации',
  'channels.colLastSuccess': 'Последний успех',
  'channels.colLastError': 'Последняя ошибка',
  'channels.colNextSync': 'Следующая синхронизация',
  'channels.notConfigured': 'Не настроено',
  'channels.syncNow': 'Синхронизировать сейчас',
  'channels.credential': 'Учётные данные',
  'channels.syncDetail': 'Детали синхронизации',
  'channels.noSyncStateYet': 'Синхронизация ещё не запускалась — добавьте учётные данные, чтобы начать.',
  'channels.consecutiveFailures': 'Подряд неудачных попыток',
  'channels.lastPolled': 'Последний опрос',
  'channels.configured': 'Настроено',
  'channels.yesWithHint': 'Да ({hint})',
  'channels.revoked': 'Отозвано',
  'channels.lastRotated': 'Последняя замена',
  'channels.never': 'Никогда',
  'channels.noCredentialSlot': 'Слот для учётных данных не найден.',
  'channels.roleCannotWrite': 'Ваша роль не позволяет изменять учётные данные.',
  'channels.rotateCredential': 'Заменить учётные данные (новое значение заменит старое)',
  'channels.setCredential': 'Задать учётные данные',
  'channels.pasteApiToken': 'Вставьте API-токен маркетплейса',
  'channels.rotate': 'Заменить',
  'channels.revoke': 'Отозвать',
  'channels.encryptedHint': 'Вставленное значение сразу шифруется и больше нигде не показывается — ни здесь, ни в ответах API.',

  // Sellers
  'sellers.active': 'Активен',
  'sellers.inactive': 'Неактивен',
  'sellers.stockSkus': 'SKU на складе',
  'sellers.openOrders': 'Открытые заказы',
  'sellers.lastActivity': 'Последняя активность',
  'sellers.activeOnly': 'Только активные',
  'sellers.emptyTitle': 'Продавцы не найдены',

  // Seller detail
  'sellerDetail.tabStock': 'Остатки',
  'sellerDetail.tabPortalUsers': 'Пользователи портала',
  'sellerDetail.tabActivity': 'Активность',
  'sellerDetail.contracts': 'Договоры',
  'sellerDetail.openOrders': 'Открытые заказы',
  'sellerDetail.openInbounds': 'Открытые приёмки',
  'sellerDetail.noStock': 'У этого продавца нет остатков.',
  'sellerDetail.skus': 'SKU',
  'sellerDetail.noOpenInbounds': 'Открытых приёмок нет.',
  'sellerDetail.number': 'Номер',
  'sellerDetail.noOpenOrders': 'Открытых заказов нет.',
  'sellerDetail.seeReturnsSection': 'Полную детализацию возвратов смотрите в разделе «Возвраты» с фильтром по этому продавцу.',
  'sellerDetail.noContractsOnFile': 'Договоров нет.',
  'sellerDetail.validFrom': 'Действует с',
  'sellerDetail.activityNotImplemented': 'Отдельная хронология активности по продавцу не реализована — см. вкладки активности в заказах/приёмках.',

  // Portal users panel
  'portalUsers.title': 'Пользователи портала продавца',
  'portalUsers.inviteUser': 'Пригласить пользователя портала',
  'portalUsers.displayName': 'Отображаемое имя',
  'portalUsers.sendInvite': 'Отправить приглашение',
  'portalUsers.noEmailProviderNote':
    'Провайдер транзакционной почты ещё не настроен — после отправки (или повторной отправки) приглашения одноразовая ссылка для настройки появится прямо здесь, скопируйте и передайте её продавцу сами.',
  'portalUsers.emptyTitle': 'У этого продавца пока нет пользователей портала',
  'portalUsers.lastLogin': 'Последний вход',
  'portalUsers.activeSessions': 'Активные сессии',
  'portalUsers.resendInvite': 'Отправить приглашение повторно',
  'portalUsers.sendPasswordReset': 'Отправить сброс пароля',
  'portalUsers.disable': 'Отключить',
  'portalUsers.enable': 'Включить',
  'portalUsers.revokeSessions': 'Отозвать сессии',
  'portalUsers.hideAudit': 'Скрыть журнал',
  'portalUsers.viewAudit': 'Показать журнал',
  'portalUsers.noAuditEvents': 'Записей журнала пока нет',
  'portalUsers.setupLinkFor': 'Одноразовая ссылка для настройки для {email}',
  'portalUsers.setupLinkWarning': 'Скопируйте эту ссылку сейчас. Повторно она показана не будет.',
  'portalUsers.setupLinkCopy': 'Скопировать ссылку',
  'portalUsers.setupLinkCopied': 'Скопировано',

  // Evidence gallery
  'evidence.onlyImagesAccepted': 'Принимаются только фото в форматах JPEG, PNG или WEBP.',
  'evidence.uploadFailed': 'Не удалось загрузить. Попробуйте снова.',
  'evidence.noPermission': 'У вас нет прав для просмотра фото по этой записи.',
  'evidence.loading': 'Загружаем фото…',
  'evidence.couldNotLoad': 'Не удалось загрузить фото.',
  'evidence.noneUploaded': 'Фото пока не загружены.',
  'evidence.view': 'Открыть',
  'evidence.altText': 'Фото: {kind}',
  'evidence.uploading': 'Загрузка…',

  // Problems
  'problems.title': 'Проблемы / Исключения',
  'problems.hasDiscrepancy': 'Есть расхождение',
  'problems.incomplete': 'Не завершено',
  'problems.unpriced': 'Без тарифа',
  'problems.awaitingDisposition': 'Ожидает решения',
  'problems.emptyTitle': 'Открытых проблем нет',
  'problems.emptyHint': 'Сейчас ничего не соответствует заданным фильтрам.',

  // Subscription
  'subscription.metricWarehouses': 'Склады',
  'subscription.metricInternalUsers': 'Внутренние пользователи',
  'subscription.metricSellerPortalUsers': 'Входы в портал продавца',
  'subscription.metricMarketplaceConnections': 'Подключения маркетплейсов',
  'subscription.metricSellers': 'Продавцы',
  'subscription.metricEvidenceStorage': 'Хранилище фото',
  'subscription.metricEvidenceRetention': 'Срок хранения фото',
  'subscription.capFbs': 'Fulfilment by Seller (FBS)',
  'subscription.capFbo': 'Fulfilment by Operator (FBO)',
  'subscription.capReturns': 'Обработка возвратов',
  'subscription.capMultiWarehouse': 'Несколько складов',
  'subscription.capMultiLegalEntity': 'Несколько юрлиц',
  'subscription.capAdvancedBilling': 'Расширенный биллинг',
  'subscription.capSellerPortal': 'Портал продавца',
  'subscription.capPublicApi': 'Доступ к публичному API',
  'subscription.capWebhooks': 'Вебхуки',
  'subscription.capDataExport': 'Экспорт данных',
  'subscription.capMigrationWizard': 'Мастер миграции',
  'subscription.capChestnyZnak': 'Маркировка «Честный знак»',
  'subscription.capEdo': 'Электронный документооборот',
  'subscription.capAdvancedReports': 'Расширенные отчёты (скоро)',
  'subscription.capVideoEvidence': 'Видео-доказательства (скоро)',
  'subscription.unlimited': 'Без ограничений',
  'subscription.nDays': '{n} дн.',
  'subscription.noSubscriptionOnFile': 'Подписка не оформлена',
  'subscription.noSubscriptionHint': 'Этой организации ещё не назначен тарифный план, поэтому ограничений нет. Обратитесь к администратору платформы, чтобы подключить план.',
  'subscription.readOnlyTitle': 'Ваша подписка доступна только для чтения',
  'subscription.reasonPrefix': 'Причина: {reason}. ',
  'subscription.readOnlyHint':
    'Все ваши данные сохранены и полностью доступны для просмотра — склады, остатки, заказы и история не затронуты. Пока подписка в статусе {status}, нельзя создавать новые склады, пользователей, входы продавцов и подключения маркетплейсов. Обратитесь к администратору платформы, чтобы восстановить подписку.',
  'subscription.pastDueBadge': 'ПРОСРОЧЕНО',
  'subscription.paymentOutstanding': 'Есть задолженность по оплате',
  'subscription.pastDueHint':
    'Всё продолжает работать в полном объёме до {date}. После этого подписка станет доступна только для чтения — ничего не удаляется, но новые склады, пользователи и подключения будут заблокированы.',
  'subscription.trialBadge': 'ТРИАЛ',
  'subscription.trialEnds': 'Триал заканчивается {date}',
  'subscription.trialHint': 'Полный доступ ко всем возможностям тарифа {plan}.',
  'subscription.currentPlanFallback': 'текущий',
  'subscription.cancelledBadge': 'ОТМЕНЕНО',
  'subscription.ends': 'Заканчивается {date}',
  'subscription.cancelledHint': 'Полный доступ сохраняется до конца уже оплаченного периода. После этого данные остаются, а подписка становится доступна только для чтения.',
  'subscription.activeBadge': 'АКТИВНА',
  'subscription.renews': 'Продление {date}.',
  'subscription.usageAndLimits': 'Использование и лимиты',
  'subscription.colResource': 'Ресурс',
  'subscription.colInUse': 'Используется',
  'subscription.colIncluded': 'Включено',
  'subscription.adjusted': 'Скорректировано',
  'subscription.notALimit': 'Не лимитировано',
  'subscription.limitReached': 'Лимит исчерпан',
  'subscription.nLeft': 'Осталось: {n}',
  'subscription.includedFeatures': 'Включённые возможности',
  'subscription.noFeaturesListed': 'Для этой организации возможности не перечислены.',
  'subscription.currentlyBlocked': 'Сейчас заблокировано',
  'subscription.changePlan': 'Изменить тариф',
  'subscription.scheduledChangeText': 'Запланирован переход на «{plan}» ({kind}), дата: {date}.',
  'subscription.changeKind.UPGRADE': 'повышение',
  'subscription.changeKind.DOWNGRADE': 'понижение',
  'subscription.interval.MONTH': 'месяц',
  'subscription.interval.YEAR': 'год',
  'subscription.waitingForApproval': 'Ожидает подтверждения от администратора платформы. Повышение тарифа вступает в силу только после подтверждения.',
  'subscription.withdrawChange': 'Отменить это изменение',
  'subscription.scheduledChangeWithdrawn': 'Запланированное изменение тарифа отменено.',
  'subscription.upgradeRequestedNotice': 'Запрошено повышение тарифа. Вступит в силу {date} после подтверждения администратором платформы.',
  'subscription.changeScheduledNotice': 'Изменение запланировано на {date}. До этого момента ничего не меняется, ничего из уже имеющегося не удаляется.',
  'subscription.moveTo': 'Перейти на',
  'subscription.selectPlan': 'Выберите тариф…',
  'subscription.requestChange': 'Запросить изменение',
  'subscription.requestChangeHint': 'Изменения вступают в силу в конце текущего периода. Повышение тарифа требует подтверждения администратора платформы, понижение — нет.',
  'subscription.cancellationWithdrawn': 'Отмена подписки отозвана.',
  'subscription.keepMySubscription': 'Сохранить подписку',
  'subscription.cancelledNotice': 'Отменено. Полный доступ сохраняется до конца текущего периода, данные не удаляются.',
  'subscription.cancelAtPeriodEnd': 'Отменить в конце периода',
  'subscription.permissionDenied': 'Подписку видят только администраторы и пользователи с ролью BILLING.',
  'subscription.loading': 'Загружаем данные подписки…',
  'subscription.planSummary': '{price} за {interval} · версия {version} · текущий период {from} – {to}',
  'subscription.history': 'История',
  'subscription.colWhen': 'Когда',
  'subscription.colChange': 'Изменение',
  'subscription.colBy': 'Кем',

  // Imports list
  'imports.newImport': 'Новый импорт',
  'imports.emptyTitle': 'Импортов пока нет',
  'imports.emptyHint': 'Начните новый импорт, чтобы загрузить продавцов, SKU или начальные остатки.',
  'imports.colFile': 'Файл',
  'imports.colType': 'Тип',
  'imports.colRows': 'Строк',
  'imports.colCreated': 'Создан',

  // Import wizard
  'importWizard.stepUpload': 'Загрузка',
  'importWizard.stepMapping': 'Сопоставление',
  'importWizard.stepValidate': 'Проверка / Конфликты',
  'importWizard.stepDryRun': 'Пробный прогон',
  'importWizard.stepCommit': 'Применение',
  'importWizard.stepSummary': 'Итог',
  'importWizard.title': 'Мастер импорта',
  'importWizard.whatAreYouImporting': 'Что вы импортируете?',
  'importWizard.csvFile': 'CSV-файл',
  'importWizard.uploading': 'Загружаем…',
  'importWizard.uploadAndParse': 'Загрузить и разобрать',
  'importWizard.mappingHint': 'Сопоставьте каждую колонку исходного файла с полем системы. Подсказки заполнены по прошлым подтверждениям.',
  'importWizard.colSourceColumn': 'Колонка файла',
  'importWizard.colSample': 'Пример',
  'importWizard.colCanonicalField': 'Поле системы',
  'importWizard.suggested': 'предложено',
  'importWizard.unmapped': 'не сопоставлено',
  'importWizard.canonicalFieldFor': 'Поле системы для {column}',
  'importWizard.applying': 'Применяем…',
  'importWizard.applyMappingAndValidate': 'Применить сопоставление и проверить',
  'importWizard.validRows': 'Корректных строк',
  'importWizard.invalidRows': 'Некорректных строк',
  'importWizard.openConflicts': 'Открытых конфликтов',
  'importWizard.conflictBannerText':
    'UPDATE_EXISTING действительно обновляет существующую запись на месте при применении. CREATE_NEW принимается только для конфликта дублирующегося штрихкода (действительно новый SKU, без переноса штрихкода); для уже существующего продавца, SKU или ячейки это отклоняется, а не разрешается наугад — угадывание новой сущности могло бы неверно отнести остатки или начисления.',
  'importWizard.noIssuesFound': 'Проблем не найдено.',
  'importWizard.colRow': 'Строка',
  'importWizard.colDetails': 'Детали',
  'importWizard.colResolve': 'Решение',
  'importWizard.choose': 'Выберите…',
  'importWizard.skipRow': 'Пропустить строку',
  'importWizard.updateExisting': 'Обновить существующую (работает как «Пропустить»)',
  'importWizard.createNew': 'Создать новую (работает как «Пропустить»)',
  'importWizard.markHardError': 'Отметить как критическую ошибку',
  'importWizard.revalidate': 'Проверить заново',
  'importWizard.continueToDryRun': 'Перейти к пробному прогону',
  'importWizard.runValidation': 'Запустить проверку',
  'importWizard.dryRunHint': 'Пока ничего не записано. Проверьте перед применением.',
  'importWizard.rowsValid': 'Корректных строк',
  'importWizard.conflicts': 'Конфликтов',
  'importWizard.entitiesToCreate': 'Будет создано записей',
  'importWizard.openingStockMovements': 'Движений начальных остатков',
  'importWizard.rowsToSkip': 'Будет пропущено строк',
  'importWizard.confirmCommitLabel': 'Я проверил(а) пробный прогон и хочу применить этот импорт.',
  'importWizard.committing': 'Применяем…',
  'importWizard.commitImport': 'Применить импорт',
  'importWizard.alreadyCommitted': 'Уже применено (без изменений)',
  'importWizard.committed': 'Применено',
  'importWizard.created': 'Создано',
  'importWizard.skipped': 'Пропущено',
  'importWizard.failed': 'Ошибок',
  'importWizard.backToImports': 'Назад к импортам',

  // Ops create-order flow + Billing lookups (no raw ids)
  'orders.newOrder': 'Новый заказ',
  'orderCreate.title': 'Новый заказ',
  'orderCreate.subtitle': 'Заказ, созданный оператором. Товар резервируется сразу при создании.',
  'orderCreate.permissionDenied': 'Создавать заказы могут только роли ADMIN и WAREHOUSE.',
  'orderCreate.seller': 'Продавец',
  'orderCreate.selectSeller': 'Выберите продавца',
  'orderCreate.warehouse': 'Склад',
  'orderCreate.selectWarehouse': 'Выберите склад',
  'orderCreate.chooseSellerWarehouseFirst': 'Выберите продавца и склад, чтобы добавить товары.',
  'orderCreate.sellerChangeResets': 'Смена продавца или склада очищает строки заказа.',
  'orderCreate.findItem': 'Найти товар (артикул, название или штрихкод)',
  'orderCreate.itemResults': 'Найденные товары',
  'orderCreate.noItemsFound': 'У этого продавца нет подходящих товаров.',
  'orderCreate.addItem': 'Добавить {sku}',
  'orderCreate.availableQty': 'доступно {qty}',
  'orderCreate.lines': 'Строки заказа',
  'orderCreate.noLines': 'Товаров пока нет — найдите товар выше и добавьте его.',
  'orderCreate.colItem': 'Товар',
  'orderCreate.colAvailable': 'Доступно',
  'orderCreate.colQty': 'Количество',
  'orderCreate.qtyFor': 'Количество для {sku}',
  'orderCreate.removeLine': 'Удалить {sku}',
  'orderCreate.remove': 'Удалить',
  'orderCreate.lineMerged': '{sku} уже есть в заказе — количество увеличено вместо добавления второй строки.',
  'orderCreate.qtyInvalid': 'Количество для {sku} должно быть целым числом больше нуля.',
  'orderCreate.qtyExceedsAvailable': 'Недостаточно остатка по {sku}: доступно {available}, запрошено {requested}.',
  'orderCreate.orderDetails': 'Данные заказа',
  'orderCreate.orderNumber': 'Номер заказа (необязательно)',
  'orderCreate.orderNumberHint': 'Оставьте пустым — номер будет сгенерирован автоматически.',
  'orderCreate.dueDate': 'Отгрузить до (необязательно)',
  'orderCreate.recipient': 'Получатель',
  'orderCreate.recipientName': 'Имя получателя',
  'orderCreate.phone': 'Телефон',
  'orderCreate.address': 'Адрес',
  'orderCreate.city': 'Город',
  'orderCreate.postalCode': 'Индекс',
  'orderCreate.country': 'Страна',
  'orderCreate.submit': 'Создать заказ и зарезервировать товар',
  'orderCreate.submitting': 'Создание…',
  'orderCreate.fixErrors': 'Исправьте отмеченные строки перед созданием заказа.',
  'orderCreate.success': 'Заказ {number} создан, товар зарезервирован.',
  'orderCreate.openOrder': 'Открыть заказ {number}',
  'orderCreate.createAnother': 'Создать ещё один заказ',
  'orderCreate.manualSource': 'Вручную (оператор)',
  'orderCreate.sellerPortalSource': 'Кабинет продавца (создан продавцом)',
  'orderCreate.err.insufficientStock': 'Недостаточно остатка по {sku}: доступно {available}, запрошено {requested}. Ничего не зарезервировано.',
  'orderCreate.err.skuNotOwned': 'Товар {sku} не принадлежит выбранному продавцу.',
  'orderCreate.err.linesRequired': 'Добавьте хотя бы один товар.',
  'orderCreate.err.numberTaken': 'Номер заказа {number} уже используется — укажите другой или оставьте поле пустым.',
  'orderCreate.err.warehouseOutOfScope': 'Вы можете создавать заказы только для своего склада.',
  'orderCreate.err.sellerArchived': 'Продавец в архиве, новые заказы для него создать нельзя.',
  'orderCreate.err.warehouseArchived': 'Склад в архиве, новые заказы на него создать нельзя.',
  'orderCreate.err.notFound': 'Продавец или склад больше не существует. Обновите страницу и выберите заново.',
  'billing.selectTariff': 'Выберите тариф, чтобы посмотреть его правила',
  'billing.noTariffsYet': 'Тарифов пока нет',
  'billing.referenceDocument': 'Документ-основание',
  'billing.docKindNone': 'Нет',
  'billing.docKindOrder': 'Заказ',
  'billing.docKindInbound': 'Поставка (приёмка)',
  'billing.searchDocumentNumber': 'Поиск по номеру документа',
  'billing.selectDocument': 'Выберите документ',
  'billing.selectSellerFirst': 'Сначала выберите продавца',
  'billing.noDocumentsForSeller': 'У этого продавца нет документов этого типа.',
  // ADR-083 - warehouses, zones, cells, codes
  'nav.warehouses': 'Склады',
  'warehouses.subtitle': 'Ваши склады, их участки и ячейки хранения.',
  'warehouses.create': 'Создать склад',
  'warehouses.orgAdminOnly': 'Создать склад может только администратор всей организации. Ваш доступ ограничен одним складом: вы можете настраивать его участки и ячейки, но не добавлять новые склады.',
  'warehouses.createdTitle': 'Склад «{name}» создан',
  'warehouses.createdBody': 'Код {code}. Места, без которых не работают операции (приёмка, сборка, упаковка, карантин, отгруженный товар), созданы автоматически. Дальше добавьте участки и ячейки хранения.',
  'warehouses.nextSetupZones': 'Настроить зоны и места хранения →',
  'warehouses.empty': 'Складов пока нет.',
  'warehouses.zonesCount': 'Участков: {n}',
  'warehouses.cellsCount': 'Ячеек: {n}',
  'warehouses.ready': 'Готов к работе',
  'warehouses.notReady': 'Не хватает системных мест',
  'warehouses.createTitle': 'Новый склад',
  'warehouses.noLegalEntity': 'Чтобы создать склад, сначала нужно юрлицо. Создайте его в разделе',
  'warehouses.name': 'Название склада',
  'warehouses.namePlaceholder': 'например, Фулфилмент Рига 2',
  'warehouses.timeZone': 'Часовой пояс',
  'warehouses.timeZoneHint': 'Используется для дат, времени отсечки и начисления хранения по дням.',
  'warehouses.systemLocationsNote': 'Вместе со складом создаются места приёмки, сборки, упаковки, карантина и отгруженного товара.',
  'warehouses.creating': 'Создаём…',
  'warehouses.createSubmit': 'Создать склад',
  'warehouses.systemTitle': 'Системные места',
  'warehouses.systemIntro': 'Системные зоны этого склада: приёмка, сборка, упаковка, карантин и отгрузка. У каждого склада свой набор. Операции используют их автоматически — ячейки для них создавать не нужно.',
  'warehouses.systemPlacesLine': 'Системные зоны: {list}',
  'warehouses.sys.RECEIVING': 'Приёмка',
  'warehouses.sys.STAGING': 'Собранный товар',
  'warehouses.sys.PACKING': 'Упаковка',
  'warehouses.sys.QUARANTINE': 'Карантин (общий)',
  'warehouses.sys.VIRTUAL': 'Отгруженный товар',
  'warehouses.sysHelp.RECEIVING': 'Сюда попадает товар при приёмке. Для заказов недоступен, пока не размещён в ячейку.',
  'warehouses.sysHelp.STAGING': 'Здесь собранный товар ждёт упаковки.',
  'warehouses.sysHelp.PACKING': 'Здесь товар упаковывают перед отгрузкой.',
  'warehouses.sysHelp.QUARANTINE': 'Место по умолчанию для повреждённого или спорного товара. Для заказов недоступно.',
  'warehouses.sysHelp.VIRTUAL': 'Не физическое место: учитывает товар, который покинул склад.',
  'warehouses.present': 'Есть',
  'warehouses.missing': 'Нет',
  'warehouses.createMissing': 'Создать недостающие',
  'warehouses.otherTitle': 'Другие места ({n})',
  'warehouses.otherIntro': 'Места, созданные до появления участков (например, загрузкой файла). Они работают как раньше и автоматически в участки не переносятся.',
  'warehouses.kind.BIN': 'ячейка хранения',
  'warehouses.kind.STAGING': 'собранный товар',
  'warehouses.kind.RECEIVING': 'приёмка',
  'warehouses.kind.PACKING': 'упаковка',
  'warehouses.kind.DOCK': 'док',
  'warehouses.kind.QUARANTINE': 'карантин',
  'warehouses.kind.VIRTUAL': 'отгруженный товар',
  'warehouses.doneTitle': 'Склад готов к работе',
  'warehouses.done.receive': 'Принимайте товар на «Приёмку» (повреждённый — сразу в ячейку брака или карантина).',
  'warehouses.done.putaway': 'Размещайте товар в ячейки хранения или FBS — только после этого он доступен для заказов.',
  'warehouses.done.pick': 'Заказы собираются из ячеек хранения и FBS, никогда — из ячеек брака и карантина.',
  'warehouses.done.isolate': 'Возвраты и повреждённый товар можно изолировать в ячейках брака и карантина.',
  'warehouses.done.tsd': 'Коды ячеек можно распечатать как этикетки и сканировать на ТСД этого склада.',
  'warehouses.step.created': 'Склад',
  'warehouses.step.zones': 'Участки',
  'warehouses.step.cells': 'Ячейки',
  'warehouses.step.ready': 'Готово',
  'warehouses.stepsLabel': 'Ход настройки',
  'warehouses.err.network': 'Связь прервалась. Ничего не задвоилось — можно нажать кнопку ещё раз.',
  'warehouses.err.outOfScope': 'Этот склад вне вашего доступа. Вы можете настраивать только свой склад.',
  'warehouses.err.quota': 'Ваш тариф не позволяет добавить ещё склады. См. раздел «Подписка».',
  'warehouses.err.timeZone': 'Выберите часовой пояс из списка.',
  'warehouses.err.checkFields': 'Проверьте поля.',
  'code.autoNote': 'Предложен автоматически по названию. Можно оставить.',
  'code.formatHint': 'Только латинские буквы, цифры и дефис, до 24 символов.',
  'code.taken': 'Этот код уже занят. Свободный вариант: {suggestion}',
  'code.free': 'Код свободен.',
  'code.useSuggested': 'Вернуть предложенный',
  'code.hintWarehouse': 'Короткое обозначение склада в списках и документах. После создания изменить нельзя.',
  'code.hintSeller': 'Короткое обозначение продавца. По нему сопоставляются строки при загрузке товаров и остатков из файла, и оно входит в номера документов, поэтому после создания изменить нельзя.',
  'code.takenOnSubmit': 'Код {code} уже занят. Попробуйте {suggestion} или оставьте автоматический.',
  'zones.title': 'Участки',
  'zones.intro': 'Разделите склад на участки: основное хранение, FBS, брак, карантин и другие. Участок — это физическая зона склада; он не меняет владельца товара и то, как заказы резервируют остатки.',
  'zones.none': 'Участков пока нет. Начните с основного хранения.',
  'zones.readOnly': 'Изменять структуру может только администратор.',
  'zones.purpose.STORAGE': 'Основное хранение',
  'zones.purpose.FBS': 'FBS',
  'zones.purpose.DEFECT': 'Брак',
  'zones.purpose.QUARANTINE': 'Карантин',
  'zones.purpose.OTHER': 'Другое хранение',
  'zones.help.STORAGE': 'Обычные ячейки хранения: размещённый здесь товар доступен для заказов.',
  'zones.help.FBS': 'Участок для товара под заказы маркетплейсов (FBS). Работает как основное хранение: тот же владелец и то же резервирование — товар отсюда может уйти в любой заказ. Отдельного FBS-остатка нет.',
  'zones.help.DEFECT': 'Изолированные ячейки для повреждённого товара. Товар здесь никогда не доступен для заказов.',
  'zones.help.QUARANTINE': 'Изолированные ячейки для товара на проверке или в споре. Для заказов недоступен.',
  'zones.help.OTHER': 'Любой другой участок хранения. Работает как основное хранение.',
  'zones.can.receive': 'принимать напрямую',
  'zones.can.store': 'хранить',
  'zones.can.pick': 'собирать заказы',
  'zones.can.isolate': 'изолировать',
  'zones.yes': 'да',
  'zones.no': 'нет',
  'zones.capabilitiesLabel': 'Что можно делать на участке',
  'zones.cellsSummary': 'Ячеек: {n} (с товаром: {withStock})',
  'zones.showCells': 'Показать ячейки',
  'zones.cellHasStock': 'есть товар',
  'zones.cellEmpty': 'пусто',
  'zones.addCells': 'Добавить ячейки',
  'zones.defaultName.STORAGE': 'Основное хранение',
  'zones.defaultName.FBS': 'FBS',
  'zones.defaultName.DEFECT': 'Брак',
  'zones.defaultName.QUARANTINE': 'Карантин',
  'zones.defaultName.OTHER': 'Хранение',
  'zones.created': 'Участок «{name}» ({code}) добавлен. Теперь создайте его ячейки.',
  'zones.add': 'Добавить участок',
  'zones.addTitle': 'Новый участок',
  'zones.purposeLabel': 'Для чего этот участок?',
  'zones.name': 'Название участка',
  'zones.code': 'Код участка (необязательно)',
  'zones.codeAuto': 'автоматически',
  'zones.codeHint': 'С него начинаются коды ячеек участка, например ST-A-01-1. Оставьте пустым — код подберётся сам (ST, FBS, DEF, QC).',
  'zones.addSubmit': 'Добавить участок',
  'zones.addCellsFor': 'Добавить ячейки в «{zone}»',
  'zones.cellsExplain': 'Код ячейки: участок-ряд-стеллаж-ярус. Укажите ряд и диапазон стеллажей и ярусов.',
  'zones.row': 'Ряд',
  'zones.racksFrom': 'Стеллажи с',
  'zones.racksTo': 'Стеллажи по',
  'zones.levelsFrom': 'Ярусы с',
  'zones.levelsTo': 'Ярусы по',
  'zones.racks': 'Стеллажи',
  'zones.levels': 'Ярусы',
  'zones.from': 'с',
  'zones.to': 'по',
  'zones.tooMany': '{n} ячеек — не больше 500 за раз. Разбейте диапазон.',
  'zones.preview': 'Будет создано ячеек: {n} ({first} … {last})',
  'zones.cellsCreatedSome': 'Создано ячеек: {n}; {existed} уже были и сохранены.',
  'zones.cellsCreated': 'Создано ячеек: {n}.',
  'zones.createCells': 'Создать ячейки',
  'zones.close': 'Закрыть',
  'zones.err.cellsTaken': 'Эти коды уже заняты другими местами: {codes}. Ничего не создано — выберите другой ряд или диапазон.',
  'zones.err.zoneCodeTaken': 'Код участка {code} уже используется на этом складе.',
  'zones.err.zoneCodeInvalid': 'Код участка — от 1 до 8 латинских букв или цифр.',
  'zones.rename': 'Переименовать',
  'zones.renameFor': 'Переименовать зону {zone}',
  'zones.renameHint': 'Меняется только отображаемое название. Назначение зоны («{purpose}») и её код {code} остаются прежними, поэтому приёмка, сборка и отчёты работают как раньше.',
  'zones.renamed': 'Зона переименована: «{name}».',
  'cells.manage': 'Управление ячейкой {code}',
  'cells.panelTitle': 'Ячейка {code}',
  'cells.codeLabel': 'Код ячейки',
  'cells.renameWarning': 'Код напечатан на этикетке ячейки. Если изменить его, уже напечатанные этикетки перестанут сканироваться — распечатайте новые.',
  'cells.pickable': 'Товар из этой ячейки можно собирать под заказы',
  'cells.sequence': 'Порядок обхода (меньше — раньше на маршруте сборки)',
  'cells.saved': 'Сохранено.',
  'cells.labelsOutdated': 'Сохранено. У ячейки есть история, поэтому перепечатайте этикетку: старая больше не сканируется. Новый код: {code}.',
  'cells.delete': 'Удалить',
  'cells.deleteHint': 'Эту ячейку ещё не использовали, поэтому её можно удалить полностью.',
  'cells.deleteBlocked.HISTORY': 'Удалить нельзя: ячейка уже использовалась (остатки, движения, задания или документы). Архивируйте её — история сохранится.',
  'cells.deleteBlocked.STOCK': 'Удалить нельзя: в ячейке есть товар.',
  'cells.deleteConfirm': 'Удалить ячейку {code} навсегда? Это нельзя отменить.',
  'cells.confirmDelete': 'Да, удалить ячейку',
  'cells.deletedDone': 'Ячейка {code} удалена.',
  'cells.archive': 'В архив',
  'cells.archiveExplain': 'Отправить ячейку {code} в архив? Из архива ячейка пропадает из приёмки, размещения, сборки, перемещений и пересчётов. История сохраняется, позже ячейку можно вернуть.',
  'cells.confirmArchive': 'Да, в архив',
  'cells.archiveBlocked.STOCK': 'В архив пока нельзя: в ячейке ещё есть товар. Сначала переместите его в другую ячейку или спишите.',
  'cells.archiveBlocked.OPEN_WORK': 'В архив пока нельзя: в ячейке есть незавершённая работа (задание или открытый пересчёт). Сначала завершите её.',
  'cells.archivedDone': 'Ячейка {code} в архиве. История сохранена.',
  'cells.archivedTitle': 'Ячейки в архиве ({n})',
  'cells.archivedIntro': 'Эти ячейки не участвуют ни в одной операции. Их история остаётся видна. Верните ячейку, чтобы снова ей пользоваться.',
  'cells.archivedOn': 'В архиве с {date}',
  'cells.restore': 'Вернуть',
  'cells.restoreAria': 'Вернуть ячейку {code}',
  'cells.restoredDone': 'Ячейка {code} возвращена.',
  'cells.err.codeTaken': 'Код {code} уже занят другим местом на этом складе.',
  'cells.err.codeInvalid': 'Код ячейки — латинские буквы, цифры и дефис, до 40 символов.',
  'cells.err.system': 'Это системное место склада (приёмка, собранные товары, упаковка, карантин, отгруженные). Его нельзя изменить, архивировать или удалить.',
  'cells.err.archived': 'Ячейка в архиве. Сначала верните её.',
  'cells.err.reserved': 'Товар в этой ячейке зарезервирован под заказы, поэтому сейчас её нельзя сделать недоступной для сборки.',
  'cells.err.purposeLocked': 'Назначение зоны, в которой уже есть ячейки, изменить нельзя. Переименуйте зону или создайте новую.',
  'labels.title': 'Этикетки ячеек',
  'labels.intro': 'Печатайте этикетки для стеллажей: код ячейки крупно и штрихкод, который не меняется при переименовании ячейки. Наклейте по этикетке на каждую ячейку и сканируйте её на терминале вместо ввода кода.',
  'labels.layoutLegend': 'Формат',
  'labels.layout.A4_8UP': 'Лист A4, 8 этикеток',
  'labels.layoutHint.A4_8UP': 'Для обычного принтера. Режьте по тонким линиям.',
  'labels.layout.LABEL_100x60': 'Этикетка 100 × 60 мм',
  'labels.layoutHint.LABEL_100x60': 'Одна этикетка на странице, для принтера этикеток.',
  'labels.scopeLegend': 'Что печатать',
  'labels.scope.WAREHOUSE': 'Весь склад (мест: {n})',
  'labels.scope.ZONE': 'Одна зона',
  'labels.scope.SELECTED': 'Выбранные ячейки',
  'labels.zonePick': 'Зона',
  'labels.selectAllIn': 'Выбрать все: {zone}',
  'labels.systemGroup': 'Системные места',
  'labels.otherGroup': 'Прочие места',
  'labels.count': 'Этикеток: {n} (не больше {max} за раз)',
  'labels.tooMany': 'Не больше {max} этикеток за раз. Выберите зону или меньше ячеек.',
  'labels.nothing': 'Пока нечего печатать.',
  'labels.download': 'Скачать этикетки',
  'labels.preparing': 'Готовим PDF…',
  'labels.done': 'Этикеток в PDF: {n}, страниц: {pages}.',
  'labels.doneArchived': 'Архивных ячеек в файле: {n}, на этикетке они помечены ARCHIVED.',
  'labels.cellBarcode': 'Штрихкод',
  'labels.printOne': 'Печать этикетки',
  'err.locArchived': 'Эта ячейка в архиве и недоступна. Выберите другую.',
  'queue.slaHelpButton': 'Что значат эти статусы?',
  'queue.slaHelpTitle': 'Как считается статус срока',
  'queue.slaHelpIntro': 'Это не приоритет, который кто-то выставляет вручную. Система пересчитывает его при каждом обновлении по сроку отгрузки заказа и текущему времени, поэтому он меняется сам и не редактируется.',
  'queue.slaHelpBasis': 'Работу нужно начать раньше срока на время, которое складу нужно, чтобы собрать, упаковать и отгрузить. Срок приходит от маркетплейса; если маркетплейс его не даёт, берётся расчётный и помечается «расчётный».',
  'queue.slaHelp.normal': 'Времени достаточно, срок неизвестен или заказ уже отгружен или отменён.',
  'queue.slaHelp.approaching_cutoff': 'До момента, когда нужно начать работу над заказом, осталось менее {approaching} ч.',
  'queue.slaHelp.urgent': 'До момента, когда нужно начать работу над заказом, осталось менее {urgent} ч.',
  'queue.slaHelp.blocked': 'Заказу что-то мешает: недобор при сборке, нет маркировки или этикетки, не закрыта упаковка, отмена после начала работы или сменился срок. Причина указана в колонке «Блокеры».',
  'queue.slaHelp.overdue': 'Срок отгрузки прошёл. Показывается и тогда, когда есть ещё и проблема.',
  'zones.err.range': 'Ряд — 1–3 буквы или цифры; стеллажи 1–99; ярусы 1–20; не больше {max} ячеек за раз.',
  'sellers.create': 'Добавить продавца',
  'sellers.createTitle': 'Новый продавец',
  'sellers.name': 'Название продавца',
  'sellers.createSubmit': 'Добавить продавца',
  'sellers.created': 'Продавец «{name}» добавлен с кодом {code}. Дальше: договор с тарифом и вход в кабинет продавца (на странице продавца).',
  // ADR-084 - staff sign-in, invitations, access
  'login.passwordFormLabel': 'Вход по email и паролю',
  'login.email': 'Email',
  'login.password': 'Пароль',
  'login.organization': 'Компания',
  'login.organizationHint': 'У вас есть доступ к нескольким компаниям. Выберите, в какую войти.',
  'login.staysSignedIn': 'Устройство помнит вход до 30 дней. На общем компьютере выходите из учётной записи.',
  'login.forgot': 'Забыли пароль?',
  'login.forgotHelp': 'Обратитесь к администратору вашей компании: в разделе «Сотрудники и роли» он выдаст одноразовую ссылку для установки нового пароля.',
  'login.useServiceKey': 'Войти по служебному ключу',
  'login.keyFormLabel': 'Вход по служебному ключу',
  'login.serviceKeyHint': 'Для интеграций и перехода действующего администратора. Сотрудники входят по email и паролю.',
  'login.backToPassword': 'Назад ко входу по email',
  'accept.inviteTitle': 'Подключение к компании в LATYNEX WMS',
  'accept.recoverTitle': 'Новый пароль',
  'accept.state.EXPIRED': 'Срок действия ссылки истёк. Попросите администратора выдать новую.',
  'accept.state.USED': 'Эта ссылка уже использована.',
  'accept.state.REVOKED': 'Ссылка отозвана. Попросите администратора выдать новую.',
  'accept.state.INVALID': 'Ссылка недействительна. Проверьте, что она скопирована целиком.',
  'accept.toLogin': 'Перейти ко входу',
  'accept.company': 'Компания',
  'accept.yourName': 'Ваше имя',
  'accept.password': 'Пароль',
  'accept.newPassword': 'Новый пароль',
  'accept.passwordHintInvite': 'Придумайте пароль не короче 10 символов. Если у вас уже есть учётная запись LATYNEX WMS с этим email — введите её пароль.',
  'accept.passwordHint': 'Не короче 10 символов и не совпадает с email. Длинную фразу легче запомнить и труднее подобрать.',
  'accept.repeat': 'Повторите пароль',
  'accept.mismatch': 'Пароли не совпадают.',
  'accept.join': 'Подключиться и войти',
  'accept.save': 'Сохранить и войти',
  'accept.signedInWarning': 'На этом устройстве уже выполнен вход. Продолжение завершит его здесь.',
  'staff.subtitle': 'Приглашайте сотрудников, назначайте роль и склад, блокируйте или отзывайте доступ.',
  'staff.subtitleScoped': 'Вы управляете сотрудниками склада «{warehouse}».',
  'staff.invite': 'Пригласить сотрудника',
  'staff.inviteTitle': 'Приглашение сотрудника',
  'staff.name': 'Имя',
  'staff.emailHint': 'По этому email сотрудник будет входить в Ops.',
  'staff.role': 'Роль',
  'staff.access': 'Доступ',
  'staff.wholeCompany': 'Вся компания (все склады)',
  'staff.scopedOnly': 'Только склад «{warehouse}» — ваш доступ ограничен им.',
  'staff.accessHint': 'Сотрудник с доступом к одному складу работает и управляет людьми только на нём.',
  'staff.review': 'Проверить',
  'staff.reviewNote': 'Вы получите одноразовую ссылку на 72 часа. Письмо не отправляется — ссылку вы передаёте сотруднику сами.',
  'staff.createInvite': 'Создать приглашение',
  'staff.edit': 'Изменить',
  'staff.linkReadyInvite': 'Приглашение для {name} готово',
  'staff.linkReadyRecover': 'Ссылка доступа для {name} готова',
  'staff.linkLabel': 'Одноразовая ссылка',
  'staff.copy': 'Скопировать ссылку',
  'staff.copied': 'Скопировано.',
  'staff.copyManually': 'Не удалось скопировать автоматически — выделите ссылку и скопируйте.',
  'staff.linkValid': 'Действует до {date}, срабатывает один раз.',
  'staff.linkOnce': 'Показывается только сейчас. Если потеряется — создайте новую, старая перестанет работать.',
  'staff.linkHandOver': 'Передайте лично или через мессенджер, которому доверяете: доступ получит тот, кто первым откроет ссылку.',
  'staff.linkNotProof': 'Ссылка не подтверждает, что человек владеет этим email.',
  'staff.linkDone': 'Готово',
  'staff.invitations': 'Приглашения',
  'staff.noInvitations': 'Приглашений пока нет.',
  'staff.expiresAt': 'ссылка действует до {date}',
  'staff.inviteStatus.PENDING': 'Ожидает',
  'staff.inviteStatus.ACCEPTED': 'Принято',
  'staff.inviteStatus.REVOKED': 'Отозвано',
  'staff.inviteStatus.EXPIRED': 'Истекло',
  'staff.newLink': 'Новая ссылка',
  'staff.revokeInvite': 'Отозвать',
  'staff.confirmRevokeInvite': 'Отозвать приглашение для {name}? Ссылка перестанет работать.',
  'staff.members': 'Сотрудники',
  'staff.you': 'вы',
  'staff.oneWarehouse': 'Один склад',
  'staff.roleGuide': 'Что может каждая роль',
  'staff.scopeHelp': 'Любую роль можно ограничить одним складом. Администратор склада управляет людьми только на этом складе и не может создать администратора всей компании.',
  'staff.roleHelp.ADMIN': 'Всё в Ops, включая сотрудников, склады, продавцов, биллинг и настройки.',
  'staff.roleHelp.WAREHOUSE': 'Приёмка, размещение, возвраты, остатки и корректировки, создание заказов.',
  'staff.roleHelp.PICKER': 'Сборка заказов; просмотр остатков и заказов.',
  'staff.roleHelp.PACKER': 'Упаковка, этикетки и отгрузка; просмотр остатков и заказов.',
  'staff.roleHelp.BILLING': 'Тарифы, начисления и счета; просмотр продавцов и подтверждений операций, без изменения складских операций.',
  'staff.roleHelp.SUPPORT': 'Просмотр продавцов, заказов, остатков и биллинга для ответов на вопросы; ничего не изменяет.',
  'staff.notFoundOrOutside': 'Сотрудник не найден или находится вне вашего доступа.',
  'staff.accessTitle': 'Доступ',
  'staff.state.ACTIVE': 'Активен',
  'staff.state.SUSPENDED': 'Заблокирован — войти не может',
  'staff.state.REVOKED': 'Доступ отозван',
  'staff.selfNote': 'Свою роль и доступ изменить нельзя — это может сделать другой администратор.',
  'staff.actionsTitle': 'Управление доступом',
  'staff.changeRole': 'Роль',
  'staff.roleChanged': 'Роль изменена. Действует со следующего действия сотрудника.',
  'staff.recoveryLink': 'Ссылка для нового пароля',
  'staff.recoveryCreated': 'Одноразовая ссылка создана. Передайте её сотруднику.',
  'staff.suspend': 'Заблокировать',
  'staff.confirmSuspend': 'Заблокировать {name}? Сотрудник сразу выйдет на всех устройствах.',
  'staff.suspended': 'Заблокирован. Сотрудник вышел на всех устройствах.',
  'staff.restore': 'Разблокировать',
  'staff.restored': 'Разблокирован. Сотрудник снова входит со своим паролем.',
  'staff.revokeAccess': 'Отозвать доступ',
  'staff.confirmRevoke': 'Отозвать доступ у {name}? Сотрудник выйдет на всех устройствах; вернуть доступ можно только новым приглашением.',
  'staff.revoked': 'Доступ отозван.',
  'staff.actionsHelp': 'Блокировка — временная. Отзыв прекращает доступ, история сохраняется. Последнего администратора компании заблокировать или понизить нельзя.',
  'staff.revokedHelp': 'Чтобы снова дать доступ, отправьте новое приглашение.',
  'staff.readOnly': 'Изменять доступ может только администратор.',
  'staff.err.network': 'Связь прервалась. Попробуйте ещё раз.',
  'staff.err.invalidCredentials': 'Неверный email или пароль. После нескольких попыток вход приостанавливается на 15 минут.',
  'staff.err.suspended': 'Ваш доступ заблокирован. Обратитесь к администратору компании.',
  'staff.err.passwordWeak': 'Придумайте пароль не короче {min} символов, не совпадающий с email.',
  'staff.err.currentPasswordWrong': 'Текущий пароль указан неверно.',
  'staff.err.linkNotValid': 'Ссылка больше не действует. Попросите администратора выдать новую.',
  'staff.err.recoveryOtherCompany': 'Этот человек работает и в другой компании, поэтому ссылка вашей компании не может задать ему пароль. Он может сменить пароль в своей учётной записи или обратиться в поддержку LATYNEX.',
  'staff.err.recoveryNotActive': 'Сначала разблокируйте сотрудника.',
  'staff.err.alreadyMember': 'У этого человека уже есть доступ к вашей компании. Откройте его в списке, чтобы изменить роль.',
  'staff.err.cannotAccept': 'Это приглашение нельзя принять с этим email. Обратитесь к администратору, который его отправил.',
  'staff.err.outOfScope': 'Это за пределами вашего склада. Вы можете управлять только сотрудниками своего склада.',
  'staff.err.self': 'Свой собственный доступ изменить нельзя.',
  'staff.err.lastAdmin': 'Это последний администратор компании — сначала назначьте другого.',
  'staff.err.quota': 'Тариф не позволяет добавить больше сотрудников. См. «Подписка».',
  'staff.err.forbidden': 'Ваша роль этого не позволяет.',
  'staff.err.tooMany': 'Слишком много попыток. Подождите минуту и попробуйте снова.',
  'staff.err.checkFields': 'Проверьте поля.',
  'settings.devicesTitle': 'Устройства',
  'settings.devicesHelp': 'Если вы входили на устройстве, к которому больше нет доступа, выйдите на всех устройствах.',
  'settings.confirmLogoutAll': 'Выйти на всех устройствах, включая это?',
  'settings.logoutAll': 'Выйти на всех устройствах',
  'settings.passwordChanged': 'Пароль изменён. Выполнен выход на других устройствах: {n}.',
  'settings.passwordSet': 'Пароль задан. Теперь можно входить по email и паролю.',
  'settings.changePassword': 'Сменить пароль',
  'settings.setPassword': 'Задать пароль',
  'settings.viaKeyNote': 'Вы вошли по служебному ключу. Задайте пароль, чтобы входить по email; ключ продолжит работать для интеграций.',
  'settings.currentPassword': 'Текущий пароль',
  // Products (SKU import)
  'skuImport.subtitle': 'Добавьте или обновите номенклатуру из файла Excel. Ничего не сохранится, пока вы не подтвердите.',
  'skuImport.step1': '1. Скачайте шаблон',
  'skuImport.templateHint': 'Одна строка — один товар; для одежды — одна строка на размер и цвет (артикул повторяется). Артикул и наименование обязательны. Вес в кг, размеры в см. До 500 строк в файле.',
  'skuImport.downloadTemplate': 'Скачать шаблон Excel',
  'skuImport.step2': '2. Выберите, что делать с уже существующими товарами',
  'skuImport.onExisting': 'Товары, которые уже есть',
  'skuImport.onExisting.skip': 'Оставить существующие товары без изменений',
  'skuImport.onExisting.update': 'Обновить существующие товары (название, вес, размеры, цвет)',
  'skuImport.step3': '3. Загрузите заполненный файл',
  'skuImport.fileHint': 'Файл Excel (.xlsx), до 512 КБ. Каждая строка проверяется до того, как что-либо сохранится.',
  'skuImport.upload': 'Загрузить файл Excel',
  'skuImport.checking': 'Проверяем файл…',
  'skuImport.previewTitle': 'Результат проверки',
  'skuImport.summary': '{new} новых, {update} к обновлению, {skip} без изменений, {error} с ошибками',
  'skuImport.nothingSaved': 'Пока ничего не сохранено. Нажмите кнопку ниже, чтобы импортировать.',
  'skuImport.hasErrors': 'В некоторых строках есть ошибки, поэтому импорт невозможен. Исправьте отмеченные строки в файле и загрузите его снова.',
  'skuImport.alreadyImported': 'Этот файл уже был импортирован, делать ничего не нужно.',
  'skuImport.col.row': 'Строка',
  'skuImport.col.code': 'Артикул',
  'skuImport.col.name': 'Наименование',
  'skuImport.col.barcode': 'Штрихкод',
  'skuImport.col.status': 'Статус',
  'skuImport.status.NEW': 'Новый',
  'skuImport.status.UPDATE': 'Обновление',
  'skuImport.status.SKIP': 'Без изменений',
  'skuImport.status.ERROR': 'Ошибка',
  'skuImport.import': 'Импортировать товары: {n}',
  'skuImport.importing': 'Импортируем…',
  'skuImport.uploadAnother': 'Загрузить другой файл',
  'skuImport.done': 'Импортировано: {created} новых, {updated} обновлено.',
  'skuImport.doneSkipped': '{skipped} без изменений.',
  'skuImport.doneReplayed': 'Этот импорт уже был выполнен ранее.',
  'skuImport.importMore': 'Импортировать ещё',
  'skuImport.row.code': 'Артикул не указан или длиннее 64 символов.',
  'skuImport.row.name': 'Наименование не указано или длиннее 200 символов.',
  'skuImport.row.weight': 'Вес должен быть числом в килограммах больше нуля (например, 0,35).',
  'skuImport.row.length': 'Длина должна быть числом в сантиметрах больше нуля (например, 12,5).',
  'skuImport.row.width': 'Ширина должна быть числом в сантиметрах больше нуля (например, 12,5).',
  'skuImport.row.height': 'Высота должна быть числом в сантиметрах больше нуля (например, 12,5).',
  'skuImport.row.barcode': 'Штрихкод указан неверно.',
  'skuImport.row.size': 'Размер указан слишком длинно.',
  'skuImport.row.color': 'Цвет указан слишком длинно.',
  'skuImport.row.variant': 'Для этой комбинации размера и цвета получился тот же код, что у другого товара. Измените артикул или добавьте столбец «Код SKU».',
  'skuImport.row.duplicateKey': 'Такой же вариант (артикул + размер + цвет) или штрихкод встречается в файле больше одного раза.',
  'skuImport.row.duplicateBarcode': 'Этот штрихкод уже есть у другого товара.',
  'skuImport.row.generic': 'Эту строку нельзя импортировать.',
  'skuImport.file.notXlsx': 'Выберите файл Excel в формате .xlsx.',
  'skuImport.file.tooLarge': 'Файл слишком большой (максимум 512 КБ).',
  'skuImport.file.macros': 'В файле есть макросы. Сохраните его как обычный .xlsx без макросов.',
  'skuImport.file.externalLinks': 'В файле есть ссылки на другие файлы. Уберите внешние ссылки и повторите.',
  'skuImport.file.embedded': 'В файле есть встроенные объекты. Уберите их и повторите.',
  'skuImport.file.corrupt': 'Файл не удалось прочитать. Убедитесь, что это корректный файл .xlsx.',
  'skuImport.file.tooManyRows': 'Слишком много строк — не больше {max} товаров в одном файле.',
  'skuImport.file.headerNotFound': 'Не найдена строка заголовков. Используйте шаблон: нужны колонки «Артикул» и «Наименование».',
  'skuImport.file.empty': 'В файле нет товаров.',
  'skuImport.file.formula': 'В строке {row} есть формула. Замените формулы обычными значениями и повторите.',
  'skuImport.file.noSheet': 'В файле нет листа для чтения.',
  'skuImport.file.alreadyUsed': 'Этот файл уже использовался для импорта.',
  'skuImport.err.hasErrors': 'В файле есть строки с ошибками, поэтому импорт невозможен. Исправьте файл и загрузите его снова.',
  'skuImport.err.stale': 'Номенклатура изменилась после проверки — загрузите файл ещё раз.',
  'skuImport.err.partial': 'Часть товаров не удалось сохранить. Загрузите файл ещё раз, чтобы увидеть, что осталось импортировать.',
  'skuImport.err.tooMany': 'Слишком много проверок за короткое время. Попробуйте через минуту.',
  'skuImport.err.network': 'Соединение потеряно. Проверьте интернет и повторите.',
  'skuImport.err.generic': 'Что-то пошло не так. Повторите чуть позже.',
  'imports.fromExcel': 'Импорт товаров из Excel',
  'skuImport.title': 'Импорт товаров из Excel',
  'skuImport.seller': 'Продавец',
  'skuImport.chooseSeller': 'Выберите продавца',
  'skuImport.sellerRequired': 'Сначала выберите продавца, затем загрузите файл.',
  'skuImport.excelCardHint': 'Скачайте шаблон, заполните его, проверьте каждую строку и подтвердите.',
  'skuImport.backToImports': 'Назад к импортам',
  'skuImport.noWrite': 'Ваша роль не позволяет импортировать товары.',
  'skuImport.err.ownerNotAllowed': 'Этого продавца нельзя использовать для импорта.',
  'skuImport.err.ownerRequired': 'Сначала выберите продавца.',

  // Pilot exceptions (stock corrections, order shortfalls, order cancel)
  'nav.exceptions': 'Исправления',
  'reason.RECOUNT': 'Пересчёт',
  'reason.FOUND': 'Нашли товар',
  'reason.LOST': 'Потеряно',
  'reason.DAMAGED': 'Повреждено',
  'reason.MISPLACED': 'Лежал не на своём месте',
  'reason.EXPIRED': 'Истёк срок годности',
  'reason.DATA_ERROR': 'Ошибка учёта',
  'reason.OTHER': 'Другое (напишите примечание)',
  'cancelReason.CLIENT_REQUEST': 'По просьбе клиента',
  'cancelReason.OUT_OF_STOCK': 'Нет в наличии',
  'cancelReason.DUPLICATE': 'Дубликат заказа',
  'cancelReason.ENTERED_BY_MISTAKE': 'Создан по ошибке',
  'cancelReason.OTHER': 'Другое (напишите примечание)',
  'stx.actionsCol': 'Действия',
  'stx.countAction': 'Пересчёт / исправить',
  'stx.moveAction': 'Переместить',
  'stx.quarantineAction': 'В карантин',
  'stx.writeOffAction': 'Списать',
  'stx.title.COUNT': 'Пересчёт и исправление остатка',
  'stx.title.MOVE': 'Перемещение остатка',
  'stx.title.QUARANTINE': 'Перемещение в карантин',
  'stx.title.WRITE_OFF': 'Списание остатка',
  'stx.fieldProduct': 'Товар',
  'stx.fieldLocation': 'Ячейка',
  'stx.fieldOwner': 'Продавец',
  'stx.systemQty': 'Количество в системе',
  'stx.onHandQty': 'На складе',
  'stx.actualQty': 'Фактическое количество (пересчитано)',
  'stx.qty': 'Количество',
  'stx.destination': 'Ячейка назначения',
  'stx.destinationPlaceholder': 'Выберите ячейку',
  'stx.destinationLoading': 'Загружаем ячейки…',
  'stx.reason': 'Причина',
  'stx.reasonPlaceholder': 'Выберите причину',
  'stx.note': 'Примечание',
  'stx.noteRequired': 'Примечание (обязательно)',
  'stx.continue': 'Продолжить',
  'stx.back': 'Назад',
  'stx.cancel': 'Отмена',
  'stx.close': 'Закрыть',
  'stx.working': 'Выполняем…',
  'stx.confirmCount': 'Подтвердить пересчёт',
  'stx.confirmMove': 'Подтвердить перемещение',
  'stx.confirmQuarantine': 'Подтвердить карантин',
  'stx.confirmWriteOff': 'Списать {qty} шт.',
  'stx.confirmCountText': 'Система {system} → факт {actual} ({delta})',
  'stx.confirmCountSame': 'Система {system} → факт {actual}: без изменений, пересчёт будет просто подтверждён.',
  'stx.confirmMoveText': 'Переместить {qty} шт. из {from} в {to}.',
  'stx.confirmQuarantineText': 'Переместить {qty} шт. из {from} в карантинную ячейку.',
  'stx.confirmWriteOffText': 'Со склада будет списано {qty} шт. товара {sku} из {from}; операция записывается в журнал движений и не отменяется.',
  'stx.reasonLine': 'Причина: {reason}',
  'stx.noteLine': 'Примечание: {note}',
  'stx.doneCount': 'Пересчитано: система {system} → факт {actual} ({delta})',
  'stx.doneCountSame': 'Пересчёт подтверждён: {qty} шт., без изменений.',
  'stx.doneMove': 'Перемещено {qty} шт. из {from} в {to}.',
  'stx.doneQuarantine': 'В карантин перемещено {qty} шт. из {from}.',
  'stx.doneWriteOff': 'Списано {qty} шт. из {from}; записано в журнал движений.',
  'stx.doneBroken': 'Затронуто резервов: {n}. Заказы, которым теперь не хватает товара, — в разделе «Исправления» → «Нехватка по заказам».',
  'stx.doneFlags': 'Отметка «нужен пересчёт» для этой ячейки снята.',
  'stx.openExceptions': 'Открыть «Исправления»',
  'stx.invalidQty': 'Введите целое число (0 или больше).',
  'stx.qtyPositive': 'Введите целое количество не меньше 1.',
  'stx.reasonMissing': 'Выберите причину.',
  'stx.noteMissing': 'Напишите короткое примечание (не меньше 3 символов).',
  'stx.destMissing': 'Выберите ячейку назначения.',
  'stx.trackedHint': 'Товары с серийным или партионным учётом нельзя исправлять здесь.',
  'found.action': 'Нашли товар',
  'found.title': 'Нашли товар',
  'found.intro': 'Нашли товар, которого в системе нет? Выберите товар (внимательно проверьте продавца) и ячейку, затем укажите, сколько нашли.',
  'found.search': 'Найдите товар (код, название или штрихкод)',
  'found.searchHint': 'Введите не меньше 2 символов.',
  'found.noHits': 'Ничего не найдено.',
  'found.seller': 'Продавец: {name}',
  'found.select': 'Выбрать',
  'found.selected': 'Выбрано: {sku} — продавец {owner}',
  'found.change': 'Выбрать другой товар',
  'found.warehouse': 'Склад',
  'found.location': 'Ячейка',
  'exc.title': 'Исправления',
  'exc.noAccess': 'У вас нет доступа к этому разделу.',
  'exc.tabCount': 'Нужен пересчёт',
  'exc.tabShortfalls': 'Нехватка по заказам',
  'exc.tabHistory': 'История',
  'exc.flagsEmpty': 'Пересчитывать нечего.',
  'exc.flagsHint': 'Ячейки появляются здесь после недобора при сборке или ручной отметки — пока их не пересчитают.',
  'exc.shortEmpty': 'Нет строк заказов с нехваткой.',
  'exc.histEmpty': 'Исправлений пока нет.',
  'exc.histFilter': 'Фильтр по товару',
  'exc.colLocation': 'Ячейка',
  'exc.colProduct': 'Товар',
  'exc.colSeller': 'Продавец',
  'exc.colWhy': 'Почему',
  'exc.colSince': 'С какого времени',
  'exc.colOrder': 'Заказ',
  'exc.colActions': 'Действия',
  'exc.colOrdered': 'Заказано',
  'exc.colPicked': 'Собрано',
  'exc.colReserved': 'В резерве',
  'exc.colMissing': 'Не хватает',
  'exc.colWhen': 'Когда',
  'exc.colWho': 'Кто',
  'exc.colKind': 'Действие',
  'exc.colChange': 'Изменение',
  'exc.colReason': 'Причина',
  'exc.colNote': 'Примечание',
  'exc.handheld': 'Терминал',
  'exc.flagReason.SHORT_PICK': 'Недобор при сборке',
  'exc.flagReason.MANUAL': 'Отмечено вручную',
  'exc.kind.COUNT': 'Пересчёт',
  'exc.kind.MOVE': 'Перемещение',
  'exc.kind.QUARANTINE': 'Карантин',
  'exc.kind.WRITE_OFF': 'Списание',
  'exc.countNow': 'Пересчитать',
  'exc.marketplaceBadge': 'Маркетплейс',
  'exc.reallocate': 'Поискать в другом остатке',
  'exc.reduce': 'Уменьшить строку до имеющегося',
  'exc.cancelLine': 'Отменить строку',
  'exc.reduceDisabled': 'Заказ маркетплейса: количество задаёт маркетплейс, здесь его уменьшить нельзя.',
  'exc.cancelLineDisabled': 'Часть строки уже собрана, поэтому отменить её нельзя.',
  'exc.title.REALLOCATE': 'Поиск в другом остатке',
  'exc.title.REDUCE': 'Уменьшение строки',
  'exc.title.CANCEL_LINE': 'Отмена строки',
  'exc.intro.REALLOCATE': 'Система попробует взять недостающие {qty} шт. из другого остатка и создать новые задания на сборку.',
  'exc.intro.REDUCE': 'Строка будет уменьшена до {keep} шт. (собрано плюс ещё в резерве). Заказ уйдёт на {qty} шт. меньше.',
  'exc.intro.CANCEL_LINE': 'Строка будет отменена, её резерв снят. По ней ещё ничего не собрано. Если это последняя открытая строка, заказ будет отменён.',
  'exc.confirm.REALLOCATE': 'Искать',
  'exc.confirm.REDUCE': 'Уменьшить строку',
  'exc.confirm.CANCEL_LINE': 'Отменить строку',
  'exc.done.REALLOCATE': 'Найдено в другом остатке: создано заданий на сборку — {n}.',
  'exc.done.REALLOCATE_NONE': 'Недостающее количество зарезервировано из другого остатка.',
  'exc.done.stillMissing': 'Всё ещё не хватает: {n} шт.',
  'exc.done.REDUCE': 'Строка уменьшена. Статус заказа: {state}.',
  'exc.done.CANCEL_LINE': 'Строка отменена. Статус заказа: {state}.',
  'ordCancel.button': 'Отменить заказ',
  'ordCancel.marketplace': 'Этот заказ пришёл с маркетплейса — его отменяет маркетплейс, а не Ops.',
  'ordCancel.title': 'Отмена заказа {number}',
  'ordCancel.consequenceRelease': 'Резервы будут сняты. Сборка ещё не начиналась.',
  'ordCancel.consequencePicked': 'Собранный товар остаётся в зоне отгрузки или упаковки: после отмены верните его в ячейку (Переместить) и подтвердите.',
  'ordCancel.confirm': 'Отменить заказ',
  'ordCancel.keep': 'Не отменять',
  'ordCancel.doneCancelled': 'Заказ отменён. Снято резервов: {n}.',
  'ordCancel.doneAfterPick': 'Заказ отменён — собранный товар нужно вернуть на склад.',
  'ordCancel.doneFinished': 'Отмена завершена — товар вернулся на склад.',
  'ordCancel.bannerTitle': 'Отменён — товар нужно вернуть на склад',
  'ordCancel.bannerText': 'Собранный товар всё ещё в зоне отгрузки или упаковки. Верните его в ячейку, затем завершите отмену.',
  'ordCancel.goMove': 'Перейти в «Остатки», чтобы вернуть товар',
  'ordCancel.finish': 'Товар вернули на полку — завершить отмену',
  'ordCancel.finishTitle': 'Завершение отмены заказа {number}',
  'ordCancel.finishText': 'Подтвердите, что весь собранный товар лежит в ячейках. Заказ станет «Отменён».',
  'ordCancel.finishConfirm': 'Подтверждаю — товар возвращён',
  'ordCancel.lineCancelled': 'Отменена',
  'err.adjustBelowHeld': 'Корректировка оставила бы на остатке меньше удерживаемого излишка ({held}). Сначала примите решение по излишку поставки и оформите его вывод, затем повторите.',
  'err.overageHeld': 'Часть товара ({held} шт.) удерживается как неразрешённый излишек приёмки и не может быть перемещена или списана. Сначала решите вопрос по излишку в карточке поставки.',
  'err.countStale': 'Остаток изменился. Сейчас в системе {current}. Пересчитайте заново.',
  'err.insufficient': 'Недостаточно товара: доступно {available}, запрошено {requested}.',
  'err.reasonRequired': 'Выберите причину.',
  'err.noteRequired': 'Напишите короткое примечание — для этой причины оно обязательно.',
  'err.writeOffOfficeOnly': 'Списание выполняется в офисе, а не на терминале.',
  'err.trackedUnsupported': 'Товары с серийным или партионным учётом нельзя исправлять здесь.',
  'err.locOtherWarehouse': 'Эта ячейка относится к другому складу.',
  'err.locNotQuarantine': 'Это не карантинная ячейка.',
  'err.locationNotAllowed': 'Эту ячейку нельзя использовать для этого действия.',
  'err.quantityInvalid': 'Некорректное количество.',
  'err.notFound': 'Не найдено — возможно, запись изменили или удалили. Обновите страницу.',
  'err.validation': 'Часть введённых данных некорректна. Проверьте поля и повторите.',
  'err.cannotCancel': 'Заказ нельзя отменить в текущем статусе ({state}).',
  'err.channelForbidden': 'Это заказ маркетплейса — его отменяет маркетплейс, а не вы здесь.',
  'err.reallocImpossible': 'В другом месте не найдено — выберите «уменьшить» или «отменить».',
  'err.reduceNotAllowed': 'Эту строку нельзя уменьшить здесь.',
  'err.linePicked': 'Часть строки уже собрана — отменить её нельзя.',
  'err.lineCancelled': 'Эта строка уже отменена.',
  'err.invalidState': 'Заказ в статусе, в котором это невозможно ({state}).',
  'err.rateLimited': 'Слишком много запросов. Подождите немного и повторите.',
  'err.forbidden': 'Ваша роль не позволяет это действие.',
  'err.network': 'Нет связи с сервером. Проверьте соединение и повторите — дважды ничего не применится.',
  'err.generic': 'Что-то пошло не так. Повторите; если повторится — сообщите администратору.',
  'sellerDetail.tabContract': 'Договор',
  'onboard.title': 'Готовность принимать заявки',
  'onboard.step.CLIENT': 'Клиент',
  'onboard.step.WAREHOUSE': 'Обслуживающий склад',
  'onboard.step.CONTRACT': 'Договор',
  'onboard.step.PORTAL': 'Доступ в Portal',
  'onboard.step.READY': 'Готов принимать заявки',
  'onboard.state.done': 'Готово',
  'onboard.state.missing': 'Не хватает',
  'onboard.detail.warehouseNone': 'Склад не назначен',
  'onboard.detail.contractNumber': '№ {number} от {date}',
  'onboard.detail.contractNumberOnly': '№ {number}',
  'onboard.detail.portalActive': 'Пользователей с доступом: {n}',
  'onboard.detail.portalPending': 'Приглашено, ещё не вошли: {n}',
  'onboard.detail.portalNone': 'Доступа пока ни у кого нет',
  'onboard.readyYes': 'Да — клиент может отправлять заявки',
  'onboard.readyNo': 'Пока нет — клиент не может отправлять заявки',
  'onboard.reason.NONE': 'Договора пока нет',
  'onboard.reason.DRAFT': 'Договор в черновике',
  'onboard.reason.EXPIRED': 'Срок договора истёк',
  'onboard.reason.SUSPENDED': 'Договор приостановлен',
  'onboard.reason.TERMINATED': 'Договор расторгнут',
  'onboard.reason.NOT_STARTED': 'Договор ещё не начал действовать',
  'onboard.missingTitle': 'Чего не хватает',
  'onboard.hint.WAREHOUSE': 'Ни один склад пока не обслуживает клиента. Назначьте склад — для этого создаётся договор с юрлицом склада.',
  'onboard.hint.PORTAL': 'Никто из сотрудников клиента пока не может войти в Portal.',
  'onboard.fix.WAREHOUSE': 'Назначить склад',
  'onboard.fix.CONTRACT': 'Оформить договор',
  'onboard.fix.PORTAL': 'Пригласить в Portal',
  'onboard.entitlement.limited': 'Складов по вашему тарифу: использовано {used} из {limit}, осталось {remaining}',
  'onboard.entitlement.unlimited': 'Складов по вашему тарифу: без ограничений',
  'onboard.channels.required': 'Маркетплейсы можно подключить только при действующем договоре.',
  'onboard.channels.free': 'Маркетплейсы можно подключить и без договора (настройка организации).',
  'contract.title': 'Договоры со складами',
  'contract.new': 'Назначить склад / новый договор',
  'contract.empty': 'Договоров пока нет. Назначьте склад, чтобы начать.',
  'contract.formNew': 'Новый договор',
  'contract.formEdit': 'Редактирование договора',
  'contract.field.warehouse': 'Склад',
  'contract.field.number': 'Номер договора',
  'contract.field.date': 'Дата договора',
  'contract.field.validFrom': 'Действует с',
  'contract.field.validTo': 'Действует до (необязательно)',
  'contract.field.state': 'Статус',
  'contract.field.tariff': 'Тариф (необязательно)',
  'contract.field.legalEntity': 'Юрлицо',
  'contract.field.warehouses': 'Склады',
  'contract.field.period': 'Срок действия',
  'contract.field.file': 'PDF договора',
  'contract.noTariff': 'Не задан',
  'contract.noNumber': 'Номер не указан',
  'contract.state.DRAFT': 'Черновик',
  'contract.state.ACTIVE': 'Действует',
  'contract.state.SUSPENDED': 'Приостановлен',
  'contract.state.ON_HOLD': 'Приостановлен',
  'contract.state.TERMINATED': 'Расторгнут',
  'contract.state.EXPIRED': 'Срок истёк',
  'contract.state.NOT_STARTED': 'Ещё не начался',
  'contract.periodOpen': '{from} — без срока',
  'contract.periodRange': '{from} — {to}',
  'contract.activationHint': 'Чтобы договор стал действующим, укажите его номер и дату.',
  'contract.terminatedNote': 'Расторгнутый договор нельзя менять. Оформите новый.',
  'contract.save': 'Сохранить договор',
  'contract.saved': 'Договор сохранён.',
  'contract.pdf.upload': 'Загрузить PDF',
  'contract.pdf.replace': 'Заменить PDF',
  'contract.pdf.download': 'Скачать PDF',
  'contract.pdf.none': 'Файл не приложен',
  'contract.pdf.hint': 'Только PDF, до 5 МБ.',
  'contract.pdf.uploaded': 'Файл загружен.',
  'contract.err.terminated': 'Договор расторгнут и больше не меняется.',
  'contract.err.activationIncomplete': 'Перед активацией укажите номер и дату договора.',
  'contract.err.alreadyActive': 'У клиента уже есть действующий договор с этим юрлицом.',
  'contract.err.numberTaken': 'Такой номер договора уже используется для этого юрлица.',
  'contract.err.document': 'Нужен файл PDF размером до 5 МБ.',
  'contract.err.period': 'Дата окончания должна быть позже даты начала.',
  'contract.err.forbidden': 'Ваша роль не позволяет это действие.',
  'contract.err.outOfScope': 'Этот договор относится к складу вне вашего доступа.',
  'contract.err.notFound': 'Не найдено. Обновите страницу.',
  'contract.err.generic': 'Не удалось сохранить. Проверьте поля и повторите.',
  'settings.channelContractTitle': 'Подключение маркетплейсов',
  'settings.channelContractLabel': 'Требовать действующий договор, чтобы клиент мог подключить маркетплейс',
  'settings.channelContractHint': 'По умолчанию включено. Отключите, если не оформляете договоры с клиентами.',
  'settings.channelContractSaved': 'Настройка сохранена.',
  'sku.article': 'Арт. {value}',
  'sku.size': 'Размер {value}',
  'sku.color': 'Цвет {value}',
  'sku.barcode': 'Штрихкод {value}',
  'sku.barcodes': 'Штрихкоды: {value}',
  'sku.barcodeMore': 'Штрихкод {value} и ещё {n}',
  'rcv.nextTitle': 'Что делать дальше',
  'rcv.tsdPath': 'На ТСД: Приёмка →',
  'rcv.copy': 'Скопировать номер',
  'rcv.copied': 'Скопировано',
  'rcv.next.EXPECTED': 'Заявка продавца принята и ждёт товар. Когда товар приехал, нажмите «Начать приёмку».',
  'rcv.next.ARRIVED': 'Товар приехал. Нажмите «Начать приёмку», чтобы открыть позиции для приёмки.',
  'rcv.next.RECEIVING': 'Идёт приёмка. Примите каждую позицию ниже кнопкой «Принять» (введите количество) или отсканируйте на ТСД. Когда все позиции приняты, нажмите «Завершить приёмку».',
  'rcv.next.RECEIVED': 'Подсчёт завершён. Проверьте расхождения и нажмите «Закрыть приёмку». Размещение по ячейкам хранения — в блоке «Размещение» ниже.',
  'rcv.next.CLOSED': 'Приёмка закрыта. Здесь больше ничего делать не нужно.',
  'rcv.next.CANCELLED': 'Эта заявка отменена.',
  'rcv.next.RETURN': 'Возвраты принимаются на странице «Возвраты» и на ТСД (Возвраты).',
  'rcv.tsd.EXPECTED': 'Или на ТСД: откройте документ, нажмите «Отметить прибытие», затем «Начать приёмку».',
  'rcv.tsd.ARRIVED': 'Или на ТСД: откройте документ и нажмите «Начать приёмку».',
  'rcv.tsd.RECEIVING': 'На ТСД: откройте документ, выберите позицию, отсканируйте ячейку {locations}, затем штрихкод товара.',
  'rcv.tsd.RECEIVED': 'На ТСД документ виден со статусом «Принято»; закрыть его можно и там.',
  'rcv.act.START': 'Начать приёмку',
  'rcv.act.ARRIVE': 'Только отметить прибытие',
  'rcv.act.FINISH': 'Завершить приёмку',
  'rcv.act.CLOSE': 'Закрыть приёмку',
  'rcv.done.START': 'Приёмка начата. Примите позиции ниже.',
  'rcv.done.ARRIVE': 'Прибытие отмечено.',
  'rcv.done.FINISH': 'Приёмка завершена.',
  'rcv.done.CLOSE': 'Приёмка закрыта.',
  'rcv.blocked.noPermission': 'Вашей роли недоступна приёмка товара. Обратитесь к руководителю склада.',
  'rcv.blocked.noZone': 'На этом складе нет ячейки приёмки, поэтому принять товар нельзя. Сначала создайте системные ячейки в разделе «Настройки» → «Склады».',
  'rcv.blocked.openDiscrepancy': 'Пока нельзя закрыть: у позиций с расхождением ({count}) не указан ответственный. Выберите его в колонке «Ответственность».',
  'rcv.blocked.linesLocked': 'Принимать позиции можно после «Начать приёмку».',
  'rcv.warn.notAllReceived': 'Не до конца приняты позиции: {count}. Приёмку всё равно можно завершить, недостача останется видна.',
  'rcv.colProduct': 'Товар',
  'rcv.colProgress': 'Принято / ожидалось',
  'rcv.colReceivedBy': 'Кто принял',
  'rcv.colTime': 'Когда',
  'rcv.colWho': 'Кто',
  'rcv.unidentified': 'Товар ещё не опознан',
  'rcv.progress': '{received} из {expected}',
  'rcv.remaining': 'Осталось принять: {n}',
  'rcv.over': 'Больше ожидаемого на {n}',
  'rcv.receive': 'Принять',
  'rcv.receiveLine': 'Принять эту позицию',
  'rcv.qty': 'Количество',
  'rcv.location': 'Куда принять',
  'rcv.discrepancy': 'Есть проблема с товаром?',
  'rcv.party': 'Кто ответственный?',
  'rcv.note': 'Комментарий',
  'rcv.find': 'Найти товар по коду, названию или штрихкоду',
  'rcv.findHint': 'Нужно, если товар неизвестен или отличается от документа. Показаны только товары этого продавца.',
  'rcv.pickedSku': 'Выбрано: {name}',
  'rcv.change': 'Изменить',
  'rcv.pick': 'Выбрать',
  'rcv.noHits': 'Ничего не найдено.',
  'rcv.submit': 'Принять',
  'rcv.cancel': 'Отмена',
  'rcv.assign': 'Сохранить',
  'rcv.overage.state.PENDING': 'Излишек ждёт решения',
  'rcv.overage.state.RESOLVED': 'Излишек разрешён',
  'rcv.overage.state.REJECTED': 'Излишек отклонён (нужна отдельная обработка)',
  'rcv.overage.state.DISPOSED': 'Излишек выведен',
  'rcv.overage.held': 'Удерживается: нельзя разместить и перемещать, к отбору недоступен.',
  'rcv.overage.resolve': 'Разрешить излишек',
  'rcv.overage.reject': 'Отклонить',
  'rcv.overage.confirmResolve': 'Принять излишек как обычный остаток? Он станет доступен к размещению.',
  'rcv.overage.dispose': 'Оформить вывод',
  'rcv.overage.disposeQty': 'Сколько выведено',
  'rcv.overage.disposeHow': 'Как выведено',
  'rcv.overage.disposeReason': 'Причина',
  'rcv.overage.disposeNote': 'Примечание (для возврата поставщику напишите здесь)',
  'rcv.overage.writeOff': 'Списание',
  'rcv.overage.quarantine': 'Карантин',
  'rcv.overage.confirmDispose': 'Подтвердите: {qty} шт. отклонённого излишка физически выведены. Удержание уменьшится ровно на это количество.',
  'rcv.overage.disposeSaved': 'Вывод излишка записан.',
  'rcv.overage.saved': 'Решение по излишку сохранено.',
  'rcv.assigned': 'Ответственный сохранён.',
  'rcv.done.line': 'Принято: {qty}. По позиции: {received} из {expected}.',
  'rcv.err.qty': 'Введите количество больше нуля (до 4 знаков после запятой).',
  'rcv.err.sku': 'Выберите товар, который фактически приехал.',
  'rcv.err.wrongItem': 'Это не тот товар, что в документе. Выберите «Неверный товар», если приехал другой товар.',
  'rcv.err.notReceiving': 'Эта заявка уже не в приёмке. Обновите страницу.',
  'rcv.err.transition': 'Этот шаг невозможен в текущем статусе. Обновите страницу.',
  'rcv.err.openDiscrepancy': 'Нельзя закрыть: у позиций с расхождением не указан ответственный.',
  'rcv.err.shortage': 'Принято меньше заявленного: недостача {short} шт. по {count} поз. Завершить приёмку по фактическому количеству? Недостающие единицы на остаток не начисляются.',
  'rcv.err.overageUnresolved': 'Нельзя закрыть: по строкам с излишком нет решения (разрешить, отклонить или отметить обработанным).',
  'rcv.err.noOverage': 'У этой строки нет излишка: решать нечего.',
  'rcv.err.disposalExceeds': 'Столько в этом месте не удерживается: проверьте количество удержанного излишка.',
  'rcv.err.disposalLocation': 'Излишек удерживается в нескольких местах: вывод оформляется по каждому отдельно (обратитесь к администратору системы).',
  'rcv.err.adjustBelowHeld': 'Корректировка оставила бы меньше удерживаемого излишка. Сначала примите решение по излишку и оформите его вывод.',
  'rcv.err.overageHeld': 'Нельзя разместить: {held} шт. удерживаются как неразрешённый излишек. Сначала примите решение по излишку (разрешить или обработать).',
  'rcv.err.ownerMismatch': 'Этот товар принадлежит другому продавцу.',
  'rcv.err.serial': 'Товар с серийными номерами принимается поштучно на ТСД.',
  'rcv.err.returnKind': 'Возвраты принимаются на странице «Возвраты».',
  'rcv.err.location': 'Выберите зону приёмки этого склада.',
  'rcv.disc.NONE': 'Без проблем',
  'rcv.disc.SHORTAGE': 'Недостача',
  'rcv.disc.OVERAGE': 'Излишек',
  'rcv.disc.WRONG_ITEM': 'Неверный товар',
  'rcv.disc.DAMAGED': 'Повреждён',
  'rcv.disc.NO_BARCODE': 'Нет штрихкода',
  'rcv.disc.EXPIRED': 'Истёк срок',
  'rcv.party.UNDECIDED': 'Не решено',
  'rcv.party.WAREHOUSE': 'Склад',
  'rcv.party.OWNER': 'Продавец',
  'rcv.party.CARRIER': 'Перевозчик',
  'rcv.party.CHANNEL': 'Канал',
  'rcv.history': 'Кто и что принял',
  'rcv.history.empty': 'Пока ничего не принято.',
  'rcv.history.byDevice': '{name} (ТСД)',
  'rcv.history.staff': 'Сотрудник',
  'rcv.activity': 'Действия в офисе',
  'rcv.log.INBOUND_ARRIVED': 'Отмечено прибытие',
  'rcv.log.INBOUND_RECEIVING_STARTED': 'Начата приёмка',
  'rcv.log.INBOUND_RECEIVING_FINISHED': 'Приёмка завершена',
  'rcv.log.INBOUND_CLOSED': 'Приёмка закрыта',
  'rcv.log.INBOUND_LINE_RECEIVED': 'Принято {qty} по позиции',
  'rcv.log.INBOUND_RESPONSIBILITY_ASSIGNED': 'Назначен ответственный: {party}',
  'rcv.log.other': 'Действие',
  'rcv.log.INBOUND_PUTAWAY': 'Размещено {qty} в ячейку {cell}',
  'rcv.flowNote': 'Товар сначала принимается в зону приёмки. После завершения приёмки его нужно разместить по ячейкам хранения.',
  'rcv.zone.RECEIVING': 'Зона приёмки склада «{warehouse}» ({code})',
  'rcv.zone.QUARANTINE': 'Карантин — только повреждённый или сомнительный товар ({code})',
  'rcv.noQuarantine': 'У склада нет зоны карантина — обратитесь к администратору.',
  'put.title': 'Размещение по ячейкам',
  'put.go': 'Перейти к размещению',
  'put.waiting': 'Ждёт размещения: {n} поз. в зоне приёмки {code} склада «{warehouse}».',
  'put.allDone': 'В зоне приёмки ничего не осталось — весь товар размещён.',
  'put.tsd': 'На ТСД: Размещение → выберите товар из списка «ждёт размещения» (зона приёмки {code}), отсканируйте ячейку хранения, затем товар.',
  'put.tasks': 'Открытых задач размещения на ТСД: {n} (к размещению {qty} шт.).',
  'put.noReceivingZone': 'У склада нет зоны приёмки — обратитесь к администратору.',
  'put.noPermission': 'Размещать товар из офиса может склад или администратор. Остальные — на ТСД.',
  'put.listLabel': 'Товар, ждущий размещения',
  'put.available': 'Можно разместить: {n} (зона {code})',
  'put.place': 'Разместить',
  'put.panel': 'Разместить товар',
  'put.search': 'Найти ячейку по коду или участку',
  'put.cell': 'Ячейки хранения',
  'put.cellZone': 'участок {zone}',
  'put.cellEmpty': 'пусто',
  'put.cellContent': 'Сейчас: {n} тов., {qty} шт. ({codes})',
  'put.noCells': 'В складе пока нет ячеек хранения. Добавьте их в разделе',
  'put.noMatch': 'Ничего не найдено.',
  'put.truncated': 'Показаны первые {n}. Уточните поиск.',
  'put.selected': 'Выбрана ячейка: {code}',
  'put.pickCell': 'Выберите ячейку хранения.',
  'put.submit': 'Разместить',
  'put.done': 'Размещено {qty} в ячейку {cell}.',
  'put.err.qtyOver': 'Можно разместить не больше {n}.',
  'put.err.destination': 'В эту ячейку размещать нельзя: выберите действующую ячейку хранения этого склада.',
  // Редактирование заявки клиентом после передачи на склад (ADR-089)
  'revNotice.title': 'Клиент изменил заявку после передачи на склад (ред. {n}, {when})',
  'revNotice.hintInbound': 'Проверьте ожидаемые количества перед приёмкой. Уже принятый товар клиент изменить не может.',
  'revNotice.hintOrder': 'Резерв уже учитывает изменение. Проверьте строки перед сборкой.',
  'revNotice.all': 'Все редакции ({n})',
  'revNotice.rev': 'Редакция {n}',
  'revNotice.submitted': 'Передана на склад',
  'revNotice.mark': 'изменено',
  'revNotice.markTitle': 'Клиент изменил заявку после передачи на склад',
  'revNotice.line.ADDED': '{sku}: добавлено, количество {to}',
  'revNotice.line.REMOVED': '{sku}: удалено (было {from})',
  'revNotice.line.CHANGED': 'Количество {sku}: {from} → {to}',
  'revNotice.fieldChange': '{field}: {from} → {to}',
  'revNotice.f.reference': 'Номер',
  'revNotice.f.expectedAt': 'Ожидаемая дата',
  'revNotice.f.dueAt': 'Дата',
  'revNotice.f.shipTo.name': 'Получатель',
  'revNotice.f.shipTo.phone': 'Телефон',
  'revNotice.f.shipTo.email': 'Эл. почта',
  'revNotice.f.shipTo.address': 'Адрес',
  'revNotice.f.shipTo.city': 'Город',
  'revNotice.f.shipTo.postalCode': 'Индекс',
  'revNotice.f.shipTo.country': 'Страна',
  'revNotice.f.shipTo.comment': 'Комментарий',
  // Scan & Receiving v2 (ADR-090): scanner mode, receiving by scan, move by scan
  'scan.mode': 'Режим сканера',
  'scan.mode.hint': 'Включите и наведите сканер на штрихкод. Он работает как клавиатура — кликать в поле не нужно.',
  'scan.status.off': 'Сканер выключен',
  'scan.status.waiting': 'Ожидание скана',
  'scan.status.active': 'Сканер активен',
  'scan.status.last': 'Последний скан: {code}',
  'scan.field': 'Отсканируйте или введите код',
  'scan.field.placeholder': 'Код со сканера или вручную',
  'scan.submit': 'Ввод',
  'scan.manual': 'Ввести вручную',
  'scan.manualOff': 'Скрыть клавиатуру',
  'scan.duplicate': 'Повторное сканирование',
  'scan.duplicate.detail': 'Тот же код пришёл сразу же ещё раз. Он учтён один раз.',
  'scan.qty': 'Количество за один скан',
  'scan.qty.dec': 'Меньше',
  'scan.qty.inc': 'Больше',
  'scan.retry': 'Повторить',
  'scan.result': 'Результат последнего скана',
  'scan.ambiguous.title': 'Этот код подходит и ячейке, и товару. Что это?',
  'scan.ambiguous.location': 'Ячейка',
  'scan.ambiguous.product': 'Товар',
  'scan.pickProduct': 'Этот штрихкод у нескольких товаров. Выберите нужный.',
  'scan.pickOwner': 'Этот товар в ячейке у нескольких продавцов. Выберите, чей.',
  'scan.err.unknown': 'Штрихкод не найден',
  'scan.err.archived': 'Ячейка в архиве',
  'scan.err.otherWarehouse': 'Ячейка другого склада',
  'scan.err.notInRequest': 'Этого товара нет в заявке',
  'scan.err.needsReceiving': 'Повреждённый, просроченный, не тот или немаркированный товар нельзя класть сразу в ячейку — примите его без размещения',
  'scan.err.sourceNotAllowed': 'Из этого места нельзя брать товар',
  'scan.err.destNotAllowed': 'В это место нельзя перемещать товар',
  'scan.err.reserved': 'Этот товар зарезервирован под заказы — его нельзя переместить',
  'scan.err.tracked': 'Товары с серийными номерами или партиями обрабатываются по одному на терминале',
  'scan.err.outOfScope': 'Этот склад недоступен вашей учётной записи',
  'scanRcv.title': 'Приёмка сканером',
  'scanRcv.request': 'Заявка {number} · {seller}',
  'scanRcv.mode.label': 'Как принимать',
  'scanRcv.mode.TO_LOCATION': 'Принимать сразу в ячейку',
  'scanRcv.mode.TO_RECEIVING': 'Принять без размещения',
  'scanRcv.dest.receiving': 'Зона приёмки {code} (склад {warehouse})',
  'scanRcv.active.cell': 'АКТИВНАЯ ЯЧЕЙКА',
  'scanRcv.active.line': '{code} · принято в неё: {n} шт.',
  'scanRcv.needCell': 'Сначала отсканируйте ячейку',
  'scanRcv.location.detected': 'Ячейка {code}',
  'scanRcv.location.detail': 'Теперь это активная ячейка. Следующие товары поступят в неё.',
  'scanRcv.location.hint': 'Это ячейка. Чтобы принимать прямо в неё, переключите режим.',
  'scanRcv.location.switch': 'Принимать в ячейку',
  'scanRcv.product.detected': 'Товар: {name}',
  'scanRcv.accepted': 'Принято: {name} +{qty} → {cell}',
  'scanRcv.accepted.progress': 'По позиции: {received} из {expected}.',
  'scanRcv.alreadyFull': 'Эта позиция уже принята полностью; лишнее учтено как излишек.',
  'scanRcv.overage': 'Больше, чем было в заявке.',
  'scanRcv.repeatHint': 'Этот товар принят дважды подряд: проверьте, что это не двойное сканирование.',
  'scanRcv.last': 'Последний скан:',
  'scanRcv.cells.title': 'Ячейки в этой приёмке',
  'scanRcv.cells.item': '{code}: {n} шт.',
  'scanRcv.progress': 'Принято {received} из {expected}',
  'scanRcv.progress.label': 'Ход приёмки',
  'scanRcv.col.expected': 'Ожидается',
  'scanRcv.col.accepted': 'Принято',
  'scanRcv.col.disc': 'Расхождение',
  'scanRcv.disc.btn': 'Расхождение',
  'scanRcv.disc.title': 'Расхождение для следующего скана',
  'scanRcv.disc.none': 'Без расхождения',
  'scanRcv.disc.next': 'Относится только к следующему отсканированному товару.',
  'scanRcv.mode.needPutaway': 'Для приёмки в ячейку нужно право размещать товар.',
  'nav.moveScan': 'Перемещение сканером',
  'moveScan.title': 'Перемещение сканером',
  'moveScan.noAccess': 'Ваша роль не позволяет перемещать товар.',
  'moveScan.warehouse': 'Склад',
  'moveScan.warehouse.choose': 'Выберите склад',
  'moveScan.warehouse.need': 'Выберите склад, чтобы начать.',
  'moveScan.steps': 'Шаги',
  'moveScan.step.SOURCE': 'Источник',
  'moveScan.step.DEST': 'Назначение',
  'moveScan.step.ITEMS': 'Товары',
  'moveScan.hint.SOURCE': 'Отсканируйте ячейку, откуда берём товар.',
  'moveScan.hint.DEST': 'Теперь отсканируйте ячейку, куда перемещаем.',
  'moveScan.hint.ITEMS': 'Сканируйте товары. Каждый скан перемещает количество, указанное ниже.',
  'moveScan.source': 'Откуда',
  'moveScan.dest': 'Куда',
  'moveScan.changeSource': 'Сменить источник',
  'moveScan.changeDest': 'Сменить назначение',
  'moveScan.quarantine': 'Карантин',
  'moveScan.quarantine.hint': 'Включайте, только чтобы переместить товар в карантинную ячейку.',
  'moveScan.sourceSet': 'Источник: {code}',
  'moveScan.destSet': 'Назначение: {code}',
  'moveScan.relocateHint': 'Ячейка {code} не применена. Чтобы сменить ячейку, нажмите «Сменить источник» или «Сменить назначение».',
  'moveScan.quarantineOff': 'Ячейка {code} — карантинная. Включите «Карантин», чтобы переместить туда товар.',
  'moveScan.sameCell': 'Источник и назначение должны быть разными ячейками.',
  'moveScan.needSource': 'Сначала отсканируйте ячейку-источник.',
  'moveScan.needDest': 'Сначала отсканируйте ячейку назначения.',
  'moveScan.moved': 'Перемещено: {name} {n} шт. из {from} в {to}',
  'moveScan.repeatHint': 'Этот товар перемещён дважды подряд: проверьте, что это не двойное сканирование.',
  'moveScan.log.title': 'Последние перемещения',
  'moveScan.log.empty': 'Пока ничего не перемещено.',
  'moveScan.log.item': '{name} · {n} шт. · {from} → {to}',
  // Единый раздел «Отгрузки» (заказы и их физические отгрузки в одном рабочем списке и карточке)
  'shipments.tabsAria': 'Способ исполнения',
  'shipments.tabAll': 'Все',
  'shipments.tabStandard': 'Стандартные',
  'shipments.tabFbs': 'FBS',
  'shipments.groupInWork': 'В работе (сборка, собран, упакован)',
  'shipments.inWork': 'В работе',
  'shipments.colOrder': 'Заказ №',
  'shipments.colTracking': 'Перевозчик / трекинг',
  'shipments.colShipment': 'Документ отгрузки',
  'shipments.viewDocuments': 'Документы физических отгрузок',
  'shipments.viewOrders': 'Назад к отгрузкам',
  'shipments.documentsTitle': 'Документы физических отгрузок',
  'shipments.sourceSeller': 'Кабинет продавца',
  'shipments.card.recipient': 'Получатель',
  'shipments.card.noRecipient': 'Получатель не указан.',
  'shipments.card.physical': 'Физические отгрузки и трекинг',
  'shipments.card.noPhysical': 'Физической отгрузки пока нет.',
  'shipments.card.openDocument': 'Открыть документ отгрузки',
  'shipments.card.reservations': 'Резерв',
  'shipments.card.noReservations': 'Под этот заказ ничего не зарезервировано.',
  'shipments.card.tasks': 'Задачи сборки',
  'shipments.card.noTasks': 'Задач сборки пока нет.',
  'shipments.card.colCell': 'Ячейка',
  'shipments.card.colQty': 'Кол-во',
  'shipments.card.colDone': 'Сделано / план',
  'shipments.card.legacyLooking': 'Открываем отгрузку…',
  'shipments.card.legacyFailed': 'Не удалось открыть эту отгрузку.',
  // Ops: создание входящей поставки для продавца
  'inbounds.newInbound': 'Новая поставка',
  'inboundCreate.title': 'Новая поставка',
  'inboundCreate.subtitle': 'Заявите поставку за продавца. Номер документа присваивает система; продавец увидит её в своём кабинете.',
  'inboundCreate.permissionDenied': 'Для создания поставки нужно право на приёмку.',
  'inboundCreate.seller': 'Продавец',
  'inboundCreate.selectSeller': 'Выберите продавца',
  'inboundCreate.warehouse': 'Склад',
  'inboundCreate.selectWarehouse': 'Выберите склад',
  'inboundCreate.sellerChangeResets': 'При смене продавца список товаров ниже очищается.',
  'inboundCreate.chooseSellerFirst': 'Сначала выберите продавца и склад.',
  'inboundCreate.findItem': 'Найти товар (код, название, артикул или штрихкод)',
  'inboundCreate.itemResults': 'Товары этого продавца',
  'inboundCreate.noItemsFound': 'У этого продавца товаров не найдено.',
  'inboundCreate.addItem': 'Добавить {sku}',
  'inboundCreate.lines': 'Товары к приёмке',
  'inboundCreate.noLines': 'Товары ещё не добавлены.',
  'inboundCreate.lineMerged': '{sku} уже в списке: количество увеличено.',
  'inboundCreate.colItem': 'Товар',
  'inboundCreate.colQty': 'План, шт.',
  'inboundCreate.qtyFor': 'Плановое количество для {sku}',
  'inboundCreate.qtyInvalid': 'Укажите целое количество больше нуля для {sku}.',
  'inboundCreate.remove': 'Убрать',
  'inboundCreate.removeLine': 'Убрать {sku}',
  'inboundCreate.details': 'Данные поставки',
  'inboundCreate.reference': 'Номер продавца (необязательно)',
  'inboundCreate.referenceHint': 'Буквы, цифры и дефисы. Если пусто, система присвоит номер по стандартной схеме.',
  'inboundCreate.expected': 'Ожидаемая дата (необязательно)',
  'inboundCreate.fixErrors': 'Сначала исправьте выделенные количества.',
  'inboundCreate.submit': 'Создать поставку',
  'inboundCreate.submitting': 'Создаём…',
  'inboundCreate.success': 'Поставка {number} создана',
  'inboundCreate.open': 'Открыть поставку {number}',
  'inboundCreate.another': 'Создать ещё',
  'inboundCreate.err.ownerNotAllowed': 'Этого продавца нельзя использовать (не найден или в архиве).',
  'inboundCreate.err.warehouseNotAllowed': 'Этот склад не обслуживает этого продавца: нет действующего договора с ним.',
  'inboundCreate.err.contractNotActive': 'У продавца нет действующего договора.',
  'inboundCreate.err.skuNotOwned': 'Товар {sku} не принадлежит этому продавцу.',
  'inboundCreate.err.linesRequired': 'Добавьте хотя бы один товар.',
  'inboundCreate.err.referenceInvalid': 'Номер может содержать только буквы, цифры и дефисы.',
  'inboundCreate.err.numberTaken': 'У этого продавца уже есть поставка с таким номером. Укажите другой или оставьте поле пустым.',
  'inboundCreate.err.notFound': 'Продавец или склад больше не существует либо вне вашего доступа. Обновите страницу и выберите заново.',
  'shipments.tabUnknown': 'Не определены',
  'shipments.colModel': 'Исполнение',
  'fulfilment.STANDARD': 'Стандартный',
  'fulfilment.FBS': 'FBS',
  'fulfilment.UNKNOWN': 'Не определён',
  'shipments.card.fulfilment': 'Способ исполнения',
  'shipments.card.unknownHint': 'У этого заказа не указан способ исполнения. Старые заказы автоматически не классифицируются: выберите верный.',
  'shipments.card.setStandard': 'Это стандартный',
  'shipments.card.setFbs': 'Это FBS',
  'shipments.card.decideConfirm': 'Указать «{model}» для заказа {number}? Позже здесь изменить это нельзя.',
  'shipments.card.decided': 'Способ исполнения сохранён.',
  'shipments.card.alreadySet': 'Заказ уже классифицирован. Обновите страницу.',
  'orderCreate.fulfilment': 'Способ исполнения',
  'orderCreate.fulfilmentSelect': 'Выберите…',
  'orderCreate.fulfilmentStandard': 'Стандартный (склад отправляет получателю)',
  'orderCreate.fulfilmentFbs': 'FBS (заказ маркетплейса, исполняется силами склада)',
  'orderCreate.fulfilmentHint': 'Обязательно. Не берётся из канала; определяет, на какой вкладке «Отгрузок» будет заказ.',
  'inboundCreate.warehouseReason.NO_WAREHOUSE': 'За этим продавцом ещё не закреплён склад.',
  'inboundCreate.warehouseReason.CONTRACT_NOT_ACTIVE': 'У продавца нет действующего договора, поэтому ни один склад не может принять его поставки.',
  'inboundCreate.warehouseReason.OUT_OF_SCOPE': 'Этот продавец не обслуживается вашим складом.',
  'inboundCreate.warehouseOnly': 'Показаны только склады, обслуживающие этого продавца по действующему договору.',
  'shipments.bulk.hint': 'Выберите старые заказы, в которых вы уверены. Автоматически ничего не классифицируется.',
  'shipments.bulk.select': 'Выбор',
  'shipments.bulk.selectAria': 'Выбрать заказ {number}',
  'shipments.bulk.selectAll': 'Выбрать все на странице',
  'shipments.bulk.count': 'Выбрано: {n}',
  'shipments.bulk.setStandard': 'Отметить выбранные как стандартные',
  'shipments.bulk.setFbs': 'Отметить выбранные как FBS',
  'shipments.bulk.confirm': 'Указать «{model}» для выбранных заказов ({n})? Уже классифицированные заказы не меняются, остальное в этих заказах не затрагивается.',
  'shipments.bulk.done': 'Классифицировано: {updated}. Уже имели этот тип: {unchanged}.',
  'shipments.bulk.conflict': 'Часть выбранных заказов уже классифицирована иначе. Ничего не изменено. Обновите список.',
  'shipments.bulk.notFound': 'Часть выбранных заказов вам больше недоступна. Ничего не изменено. Обновите список.',
  'nav.putaway': 'Размещение',
  'inbounds.colAwaitingPutaway': 'Ожидает размещения',
  'putaway.awaitingBadge': 'Ожидает размещения: {n}',
  'putaway.awaitingHint': 'Принятый товар, который ещё лежит в зоне приёмки склада и недоступен для отбора, пока его не разместят (общий остаток товара).',
  'putaway.title': 'Размещение',
  'putaway.subtitle': 'Принятый товар лежит в зоне приёмки и не отбирается. Он становится доступным только после подтверждённого перемещения в ячейку хранения сканированием.',
  'putaway.plan': 'Сформировать задания',
  'putaway.planned': 'Создано заданий: {n}, единиц: {qty}.',
  'putaway.statUnplanned': 'Ждёт задания',
  'putaway.statTasks': 'Открытых заданий',
  'putaway.statInWork': 'В работе',
  'putaway.statHeld': 'Удержанный излишек (ждёт решения)',
  'putaway.awaitingTitle': 'Ожидает размещения',
  'putaway.awaitingEmpty': 'В зоне приёмки ничего не ждёт размещения.',
  'putaway.tasksTitle': 'Задания размещения',
  'putaway.tasksEmpty': 'Открытых заданий размещения нет.',
  'putaway.colCell': 'Ячейка приёмки',
  'putaway.colOnHand': 'В зоне',
  'putaway.colHeld': 'Удержано',
  'putaway.colPlanned': 'В заданиях',
  'putaway.colUnplanned': 'Не запланировано',
  'putaway.colSince': 'С',
  'putaway.colDone': 'Размещено / план',
  'putaway.colHolder': 'Выполняет',
  'putaway.colCreated': 'Создано',
  'putaway.serial': 'серийный',
  'putaway.serialHint': 'размещается по одной единице на ТСД',
  'putaway.waitsForReturn': 'ждёт решения по возврату',
  'putaway.heldHint': 'Нерешённый излишек приёмки: не предлагается к размещению, пока офис не примет решение.',
  'putaway.leaseLapsed': 'время удержания истекло',
  'putaway.release': 'Освободить',
  'putaway.cancelTask': 'Отозвать',
  'putaway.confirmAria': 'Подтвердите действие с заданием',
  'putaway.confirmRelease': 'Освободить задание, чтобы его мог взять другой ТСД?',
  'putaway.confirmCancel': 'Отозвать задание? Ничего не перемещается; единицы будут ждать нового задания.',
  'putaway.released': 'Задание снова свободно.',
  'putaway.cancelled': 'Задание отозвано.',
};
